/**
 * talking-avatar/app.js — free, in-browser 3D talking-head VIDEO GENERATOR.
 *
 * Pipeline (100% same-origin assets → RU edge serves them, no RKN-blocked CDN):
 *   text → server TTS (MS Edge voices, all 9 locales) → MP3 → AudioBuffer
 *        → amplitude-envelope lip-sync (deterministic f(t) from the audio itself,
 *          so morphs never desync between preview and export, any language)
 *        → Three.js renders a CC0 GLB avatar (mpfb, ARKit/Oculus visemes)
 *        → export: WebCodecs (CanvasVideoExport, fast/offline) where available,
 *          else a realtime MediaRecorder fallback (iOS Safari has no WebCodecs)
 *        → MP4 / WebM → download. Audio-only (MP3) download also offered.
 *
 * Morphs use one f(t); camera/scene is a preview artifact locked to the active
 * size/bg at export time.
 *
 * Deps (blade, in order): THREE r147 global, THREE.GLTFLoader, THREE.OrbitControls,
 * window.CanvasVideoExport.
 */
(function () {
    'use strict';

    var SLUG = 'talking-avatar';
    // Avatar registry — data-driven. type 'glb' = a CC0/MIT GLB with ARKit/Oculus
    // visemes; type 'gnm' = our parametric head (asset shared with /face-maker).
    // 2026-07-16 (Farid, after #934): the gnm HEADS are pulled from the picker —
    // Aria is a full-body clothed character and a floating head next to her reads
    // broken; the user asked for a full-body MALE. The gnm loader/lip-sync code
    // below stays (a full-body male entry will reuse the 'glb' path once a
    // commercially-clean model lands; see LICENSE.md sourcing rules).
    var AVATARS = [
        // `packed` = the same model through gltfpack (meshopt + quantization): Aria
        // 17.9 → 2.4 MB, Максим 10.5 → 5.8 MB (2026-08-26, tests/playwright/
        // talking-avatar-load-bench.mjs). Textures were never the weight — Aria
        // carries 0.5 MB of webp and 17 MB of geometry/morph targets. Picked when
        // the meshopt decoder is on the page; `?ta_plain=1` forces the plain file
        // so the two can be measured against each other on one build.
        { id: 'aria', type: 'glb', url: '/vendor/talking-avatar/mpfb.glb', packed: '/vendor/talking-avatar/mpfb-mo.glb', label: 'Aria', i18n: 'av_aria' },
        // Full-body clothed male (#934) — readyplayerme/visage demo asset (MIT repo),
        // 52 ARKit + 15 visemes verified. strip: the bundled helmet/face-mask meshes
        // are removed at load so the head (fully textured underneath) shows.
        { id: 'maxim', type: 'glb', url: '/vendor/talking-avatar/male.glb', packed: '/vendor/talking-avatar/male-mo.glb', label: 'Максим', i18n: 'av_male', strip: ['Wolf3D_Headwear', 'Wolf3D_Facewear'] }
    ];
    var avatarIdx = 0;
    // «Свой аватар» (suggestion #2200, 2026-08-26): any GLB carrying ARKit/Oculus
    // visemes — Ready Player Me exports one from a selfie. The bytes live in THIS
    // browser's IndexedDB (tool-draft.js); the entry joins AVATARS only while a
    // model is actually loaded, so the picker never advertises an empty slot.
    // A model without an open-mouth morph is refused up front (scanMorphs runs on
    // the parsed scene BEFORE it replaces the one on stage) — a silent avatar
    // that "loaded fine" would be the same failure as the old dropped telemetry.
    var CUSTOM_ID = 'custom';
    var customAvatar = null;     // { name, bytes, buffer (ArrayBuffer), visemes }
    var customStore = null;      // __toolDraft store, resolved lazily; false = unavailable
    var TTS_URL = (typeof window !== 'undefined' && window.__TA_TTS_URL) || '';
    var TTS_FMT = 'audio-24khz-48kbitrate-mono-mp3';
    // Load watchdog (2026-08-26). Was a flat 90 s timer — which on the 17.9 MB
    // Aria gave up on every link under ~1.6 Mbit/s while bytes were still
    // arriving (bench: 1 Mbit → timeout at 103 s; 1.6 Mbit → 104 s, one hiccup
    // from failing). A download that is PROGRESSING is never the problem, so the
    // watchdog now fires on STALL (no progress event for 45 s) with a far hard
    // cap; a failed attempt is retried — first bypassing the caches, then, on the
    // RU origin, from the CDN twin (tembrica.com answers with ACAO *).
    var AVATAR_STALL_MS = 45000;
    var AVATAR_HARD_CAP_MS = 15 * 60000;
    var AVATAR_RETRIES = 2;
    var ALT_ORIGIN = (typeof location !== 'undefined' && location.hostname === 'timbrica.com') ? 'https://tembrica.com' : '';
    var FORCE_PLAIN = (typeof location !== 'undefined' && /[?&]ta_plain=1/.test(location.search));
    function csrfToken() { var m = document.querySelector('meta[name="csrf-token"]'); return m ? m.getAttribute('content') : ''; }

    if (typeof THREE === 'undefined') { console.error('[talking-avatar] THREE not loaded'); return; }

    /* ───────────────────────── voices ───────────────────────── */
    // 29 server-side Silero RU voices (blade-injected; ids like 'ru_oksana') — they
    // synthesize via /api/tts/synthesize and share the site-wide daily char quota.
    var SILERO = (typeof window !== 'undefined' && window.__TA_SILERO) || [];
    function isSileroVoice(id) { return id && id.indexOf('ru_') === 0; }
    var LANG_LABEL = { en: 'English', ru: 'Русский', ko: '한국어', ar: 'العربية', id: 'Bahasa Indonesia', pt: 'Português', de: 'Deutsch', es: 'Español', fr: 'Français', uk: 'Українська', tr: 'Türkçe' };
    var LANG_VOICES = {
        en: [['en-US-AriaNeural', 'Aria · F'], ['en-US-GuyNeural', 'Guy · M'], ['en-GB-SoniaNeural', 'Sonia · F · UK'], ['en-GB-RyanNeural', 'Ryan · M · UK']],
        ru: [['ru-RU-SvetlanaNeural', 'Светлана · Ж'], ['ru-RU-DmitryNeural', 'Дмитрий · М']],
        ko: [['ko-KR-SunHiNeural', 'SunHi · F'], ['ko-KR-InJoonNeural', 'InJoon · M']],
        ar: [['ar-SA-ZariyahNeural', 'Zariyah · F'], ['ar-SA-HamedNeural', 'Hamed · M']],
        id: [['id-ID-GadisNeural', 'Gadis · F'], ['id-ID-ArdiNeural', 'Ardi · M']],
        pt: [['pt-BR-FranciscaNeural', 'Francisca · F · BR'], ['pt-BR-AntonioNeural', 'Antônio · M · BR'], ['pt-PT-RaquelNeural', 'Raquel · F · PT']],
        de: [['de-DE-KatjaNeural', 'Katja · F'], ['de-DE-ConradNeural', 'Conrad · M']],
        es: [['es-ES-ElviraNeural', 'Elvira · F · ES'], ['es-ES-AlvaroNeural', 'Álvaro · M · ES'], ['es-MX-DaliaNeural', 'Dalia · F · MX']],
        fr: [['fr-FR-DeniseNeural', 'Denise · F'], ['fr-FR-HenriNeural', 'Henri · M']],
        uk: [['uk-UA-PolinaNeural', 'Поліна · Ж'], ['uk-UA-OstapNeural', 'Остап · Ч']],
        /* K = Kadın, E = Erkek — the gender tag is localized here, as the
           Russian and Ukrainian rows already do with Ж/М and Ж/Ч. */
        tr: [['tr-TR-EmelNeural', 'Emel · K'], ['tr-TR-AhmetNeural', 'Ahmet · E']]
    };
    var SIZES = { '1:1': { w: 1080, h: 1080 }, '9:16': { w: 1080, h: 1920 }, '16:9': { w: 1920, h: 1080 } };

    /* ───────────────────────── DOM / state ───────────────────────── */
    var $ = function (id) { return document.getElementById(id); };
    var els = {};
    /* ── how much text one avatar video may carry ──────────────────────────
       Same per-request allowance as /text-to-speech (tiers → tts.max_chars_per_request,
       delivered as __tierLimits.tts_limit), because it is the same voice-over quota
       and the same daily character pot. Until 2026-08-10 this was a hard 1500 for
       everyone: Премиум pays for 30 000 chars per request and got the same box as a
       guest.

       Two clamps, both deliberate:

       FLOOR 3000 — /text-to-speech's ladder is INVERTED on purpose (anon 3000 above
       verified 500) as an anti-farming measure against throwaway verified accounts.
       Copying it verbatim would have CUT verified users from 1500 to 500. It also
       would have bought nothing: their daily pot is 1000 chars, so the per-request
       number never binds for them anyway.

       CEILING — this tool renders VIDEO, which /text-to-speech does not, and that is
       a constraint the char ladder knows nothing about. Measured 2026-08-10 on a real
       full-length job: 1344 chars → 91 s of speech (≈15 chars per second of video),
       exported at 2.01× realtime on software GL. So the ladder's 30 000 would mean a
       ~34-minute video and an export the user has to keep a tab open for. */
    var MAX_CHARS_FLOOR = 3000;
    var MAX_CHARS_CEIL = 10000;      // ≈11 min of video — see AVATAR_EXPORT note above
    var MAX_CHARS = MAX_CHARS_FLOOR;
    function applyTierChars(limits) {
        var per = limits && typeof limits.tts_limit === 'number' ? limits.tts_limit : 0;
        var next = Math.min(MAX_CHARS_CEIL, Math.max(MAX_CHARS_FLOOR, per || 0));
        if (next === MAX_CHARS) return;
        MAX_CHARS = next;
        if (els.text) els.text.maxLength = MAX_CHARS;
        if (els.charCount) { var n = (els.text && els.text.value || '').length; els.charCount.textContent = n + ' / ' + MAX_CHARS; }
    }

    var scene, camera, renderer, controls;
    var avatarRoot = null, headBone = null, tickId = 0, disposed = false, webglOk = false;
    var morphRoles = { open: [], wide: [], round: [], blink: [] };
    var hasMorphs = false, camZoom = 1.0;
    var morphByName = {}, morphNames = [];         // lowerName -> [{infl,index}] across meshes; + display-case names
    var manualMorphs = {}, manualPose = null;      // user's manual base pose (expression morphs + head rotation)
    var manualActive = false, idleAnim = true;     // manualActive pauses default idle motion while posing

    /* ── GNM parametric avatar (shared /vendor/gnm asset) ──
       gnmData caches the downloaded model once per page; each gnm avatar builds a
       BASE vertex buffer (template + identity + static expression, lip/blink comps
       zeroed) and the per-frame pass adds only the lip-sync + blink deltas. */
    var GNM = (typeof window !== 'undefined' && window.__TA_GNM) || { bases: ['/vendor/gnm/'], v: 1 };
    var GNM_LIP = { open: { k: 16, s: -1 }, round: { k: 17, s: 1 }, wide: { k: 20, s: -1 } };
    var GNM_BLINK = [{ k: 1, s: -1 }, { k: 9, s: -1 }];
    var GNM_AWAKE = [{ k: 0, v: 1.2 }, { k: 8, v: 1.2 }];
    var GNM_SKINS = ['#f2d7c4', '#e8c3a8', '#d1a284', '#b97f58', '#8d5a3b', '#5d3a26'];
    var GNM_EYES = ['#57351e', '#8b6a3f', '#4a7440', '#4f7fa8', '#7d8a94', '#2c1e14'];
    var gnmData = null;                 // promise → {man,template,idF,exF,regions,tris,V,T,K_ID,K_EX}
    var gnmMode = false, gnmBase = null, gnmPos = null, gnmGeo = null;

    function gnmFetch(base, name, v) {
        return fetch(base + name + '?v=' + v, { mode: base.indexOf('//') !== -1 ? 'cors' : 'same-origin' })
            .then(function (r) { if (!r.ok) throw new Error(name + ' ' + r.status); return name.indexOf('.json') !== -1 ? r.json() : r.arrayBuffer(); });
    }
    function gnmLoad() {
        if (gnmData) return gnmData;
        var attempt = Promise.reject(new Error('no bases'));
        GNM.bases.forEach(function (base) {
            attempt = attempt.catch(function () {
                return gnmFetch(base, 'gnm-manifest.json', GNM.v).then(function (man) {
                    return Promise.all([
                        gnmFetch(base, man.files.template, GNM.v), gnmFetch(base, man.files.id, GNM.v),
                        gnmFetch(base, man.files.ex, GNM.v), gnmFetch(base, man.files.tris, GNM.v),
                        gnmFetch(base, man.files.regions, GNM.v)
                    ]).then(function (b) {
                        var idI16 = new Int16Array(b[1]), exI16 = new Int16Array(b[2]);
                        var idF = new Float32Array(idI16.length), exF = new Float32Array(exI16.length), i;
                        for (i = 0; i < idF.length; i++) idF[i] = idI16[i] * man.id_scale;
                        for (i = 0; i < exF.length; i++) exF[i] = exI16[i] * man.ex_scale;
                        return { man: man, V: man.V, T: man.T, K_ID: man.K_id, K_EX: man.K_ex, template: new Float32Array(b[0]), idF: idF, exF: exF, tris: new Uint32Array(b[3]), regions: new Uint8Array(b[4]) };
                    });
                });
            });
        });
        gnmData = attempt.catch(function (e) { gnmData = null; throw e; });
        return gnmData;
    }
    function gnmHex(h) { var n = parseInt(h.slice(1), 16); return [(n >> 16 & 255) / 255, (n >> 8 & 255) / 255, (n & 255) / 255]; }
    function gnmColors(d, pal) {
        var skin = gnmHex(GNM_SKINS[(pal && pal.s) | 0] || GNM_SKINS[2]);
        var iris = gnmHex(GNM_EYES[(pal && pal.e) | 0] || GNM_EYES[0]);
        var lips = [Math.min(1, skin[0] * 0.92), skin[1] * 0.72, skin[2] * 0.83];
        var FIX = { 2: [0.86, 0.47, 0.5], 3: [0.93, 0.91, 0.86], 4: [0.8, 0.4, 0.42], 5: [0.94, 0.93, 0.92], 7: [0.04, 0.04, 0.05] };
        var out = new Float32Array(d.V * 3);
        for (var i = 0; i < d.V; i++) {
            var r = d.regions[i], c = (r === 0) ? skin : (r === 1) ? lips : (r === 6) ? iris : (FIX[r] || skin);
            out[i * 3] = c[0]; out[i * 3 + 1] = c[1]; out[i * 3 + 2] = c[2];
        }
        return out;
    }
    // face: {id:[...], ex:[...], pal:{s,e}} → static base positions (lip/blink zeroed)
    function gnmBuildBase(d, face) {
        var base = new Float32Array(d.template), i, k, c, n = d.V * 3;
        var idA = (face && face.id) || [], exA = ((face && face.ex) || []).slice();
        GNM_AWAKE.forEach(function (a) { if (!exA[a.k]) exA[a.k] = a.v; });
        [GNM_LIP.open.k, GNM_LIP.round.k, GNM_LIP.wide.k, GNM_BLINK[0].k, GNM_BLINK[1].k].forEach(function (k2) {
            // blink comps double as eyelid openers (awake) — zero only the LIP comps,
            // and cap the blink comps so the frame-time blink still closes fully
            if (k2 === GNM_BLINK[0].k || k2 === GNM_BLINK[1].k) return;
            exA[k2] = 0;
        });
        for (k = 0; k < Math.min(d.K_ID, idA.length); k++) { c = +idA[k] || 0; if (!c) continue; var o = k * n; for (i = 0; i < n; i++) base[i] += c * d.idF[o + i]; }
        for (k = 0; k < Math.min(d.K_EX, exA.length); k++) { c = +exA[k] || 0; if (!c) continue; var o2 = k * n; for (i = 0; i < n; i++) base[i] += c * d.exF[o2 + i]; }
        return base;
    }
    // per-frame: base + lip-sync + blink deltas (only 4-5 comps → cheap)
    function gnmFrame(d, open, wide, round, blink) {
        gnmPos.set(gnmBase);
        var n = d.V * 3, i;
        function add(k, amt) { if (!amt) return; var o = k * n; for (i = 0; i < n; i++) gnmPos[i] += amt * d.exF[o + i]; }
        add(GNM_LIP.open.k, GNM_LIP.open.s * open * 2.4);
        add(GNM_LIP.wide.k, GNM_LIP.wide.s * wide * 1.6);
        add(GNM_LIP.round.k, GNM_LIP.round.s * round * 1.4);
        if (blink > 0.02) { add(GNM_BLINK[0].k, GNM_BLINK[0].s * blink * 2.6); add(GNM_BLINK[1].k, GNM_BLINK[1].s * blink * 2.6); }
        if (gnmGeo && gnmGeo.attributes.position) { gnmGeo.attributes.position.needsUpdate = true; gnmGeo.computeVertexNormals(); }
    }
    // The visitor's saved /face-maker face (same browser) → optional 'custom' avatar.
    function gnmSavedFace() {
        try {
            if (!window.__toolSettings) return null;
            var raw = window.__toolSettings.load('face-maker', 'face', null);
            if (!raw) return null;
            var s = JSON.parse(raw);
            if (!s || !s.id) return null;
            return { id: s.id, ex: s.ex || [], pal: s.pal ? { s: s.pal.s, e: s.pal.e } : null };
        } catch (_) { return null; }
    }

    var audioCtx = null;                 // ONE shared context (decode + preview) — closed only on dispose
    var currentBuffer = null, currentAudioBlob = null;
    // Identity of the spoken take currentBuffer holds (voice + prosody + script),
    // stamped when it is synthesised — the unit the daily cap counts. See onExport.
    var currentTakeKey = null;
    function takeKeyOf(parts) {
        var str = parts.join('|'), h = 5381;
        for (var i = 0; i < str.length; i++) h = ((h * 33) ^ str.charCodeAt(i)) >>> 0;
        return 'take:' + h.toString(36) + ':' + str.length;
    }
    // Mouth-shape tracks for the current voice-over: three channels at ENV_FPS,
    // built by talking-avatar/lipsync.js from Edge's viseme metadata when the
    // voice reports it, and estimated acoustically when it doesn't.
    var lip = null, ENV_FPS = 120, durSec = 0;
    var previewSrc = null, previewStart = 0, previewing = false;
    var speakInProgress = false, exporting = false, exportAbort = null, ttsAbort = null;
    var captions = [], _gradTex = null;

    function ae(event, data) { try { if (window.__ae) window.__ae(event, data || {}); } catch (_) {} }
    function toast(m, t) { try { (window.__toast || function () {})(m, t || 'info'); } catch (_) {} }
    function L(key, fallback) { try { var d = window.__TA_I18N || {}; return d[key] || fallback; } catch (_) { return fallback; } }

    function getAudioCtx() {
        if (!audioCtx || audioCtx.state === 'closed') {
            var AC = window.AudioContext || window.webkitAudioContext;
            if (!AC) throw new Error('no_audio_api');
            audioCtx = new AC();
        }
        if (audioCtx.state === 'suspended') { try { audioCtx.resume(); } catch (_) {} }
        return audioCtx;
    }

    /* ───────────────────────── scene ───────────────────────── */
    function initScene() {
        var wrap = els.canvasWrap;
        try {
            // Probe WebGL before constructing the renderer so an unsupported browser
            // gets a friendly message instead of a hard throw.
            var probe = document.createElement('canvas');
            if (!(probe.getContext('webgl2') || probe.getContext('webgl') || probe.getContext('experimental-webgl'))) throw new Error('no_webgl');
            renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true, alpha: false });
        } catch (e) {
            webglOk = false;
            setStatus(L('err_webgl', 'Your browser does not support 3D (WebGL). Try an up-to-date Chrome, Edge, Safari or Firefox.'));
            if (els.speak) els.speak.disabled = true;
            ae('ta_webgl_unavailable', { msg: (e && e.message || '').slice(0, 60) });
            return;
        }
        webglOk = true;

        scene = new THREE.Scene();
        scene.background = new THREE.Color(bgColor());
        camera = new THREE.PerspectiveCamera(22, 1, 0.1, 100);
        renderer.setClearColor(new THREE.Color(bgColor()), 1);
        renderer.outputEncoding = THREE.sRGBEncoding;
        if ('physicallyCorrectLights' in renderer) renderer.physicallyCorrectLights = true;
        renderer.domElement.id = 'ta-gl';
        renderer.domElement.setAttribute('aria-label', L('aria_preview', '3D avatar preview'));
        wrap.appendChild(renderer.domElement);

        scene.add(new THREE.HemisphereLight(0xffffff, 0x4a4a55, 1.05));
        var key = new THREE.DirectionalLight(0xffffff, 1.25); key.position.set(1.2, 2.2, 2.4); scene.add(key);
        var rim = new THREE.DirectionalLight(0x9fc2ff, 0.5); rim.position.set(-1.8, 1.4, -1.6); scene.add(rim);

        if (THREE.OrbitControls) {
            controls = new THREE.OrbitControls(camera, renderer.domElement);
            controls.enablePan = false; controls.enableDamping = true; controls.dampingFactor = 0.08;
            controls.minDistance = 0.2; controls.maxDistance = 4.0; controls.rotateSpeed = 0.6;
        }
        /* The GPU can take the context back at any moment (a phone under
           memory pressure — Sentry TIMBRICA-FRONTEND-KQ, TECNO, Android 15).
           webglcontextlost is dispatched ASYNC, so three r147's own guard has
           not flipped when the next frame compiles a fresh program and its
           unguarded getProgramInfoLog(...).trim() reads null. tick() asks the
           context itself (isContextLost — the only synchronous truth) and the
           page says what happened; preventDefault keeps the context restorable. */
        renderer.domElement.addEventListener('webglcontextlost', function (ev) {
            try { ev.preventDefault(); } catch (_) {}
            setStatus(L('err_webgl_lost', 'The 3D view was lost — the browser freed the graphics memory. It returns by itself; if not, reload the page.'));
            ae('ta_webgl_lost', {});
        });
        renderer.domElement.addEventListener('webglcontextrestored', function () {
            setStatus('');
            ae('ta_webgl_restored', {});
        });
        applyBackground();
        resizePreview();
        window.addEventListener('resize', resizePreview);
        tickId = requestAnimationFrame(tick);
    }

    function bgColor() { return (els.bg && els.bg.value) || '#0f1830'; }
    function bgColor2() { return (els.bg2 && els.bg2.value) || '#243b6b'; }
    function bgMode() { return (els.bgmode && els.bgmode.value) || 'solid'; }
    function currentSize() { return SIZES[(els.size && els.size.value) || '1:1'] || SIZES['1:1']; }

    /* ── background: solid Color or a vertical-gradient CanvasTexture as the
       scene background (rendered by WebGL → identical in preview + every export
       path, no compositing) ── */
    function makeGradientTexture(c1, c2) {
        // 4×512 (power-of-two) + ClampToEdge + Linear, no mipmaps → renders
        // reliably as a background texture on iOS WebGL1 (avoids the NPOT/solid-fill trap).
        var cv = document.createElement('canvas'); cv.width = 4; cv.height = 512;
        var g = cv.getContext('2d'); var grd = g.createLinearGradient(0, 0, 0, 512);
        grd.addColorStop(0, c1); grd.addColorStop(1, c2); g.fillStyle = grd; g.fillRect(0, 0, 4, 512);
        var tex = new THREE.CanvasTexture(cv);
        tex.wrapS = THREE.ClampToEdgeWrapping; tex.wrapT = THREE.ClampToEdgeWrapping;
        tex.minFilter = THREE.LinearFilter; tex.magFilter = THREE.LinearFilter; tex.generateMipmaps = false;
        tex.encoding = THREE.sRGBEncoding; tex.needsUpdate = true; return tex;
    }
    function applyBackground() {
        if (!scene || !renderer) return;
        if (bgMode() === 'gradient') {
            if (_gradTex) { try { _gradTex.dispose(); } catch (_) {} }
            _gradTex = makeGradientTexture(bgColor(), bgColor2());
            scene.background = _gradTex;
        } else {
            scene.background = new THREE.Color(bgColor());
        }
        renderer.setClearColor(new THREE.Color(bgColor()), 1);
        if (els.bg2) els.bg2.style.display = (bgMode() === 'gradient') ? '' : 'none';
    }

    /* ── burned-in captions: split text into time-proportioned segments; drawn on
       the 2D export canvas (both WebCodecs + MediaRecorder paths) and mirrored in
       a DOM overlay for the live preview ── */
    function captionsOn() { return !!(els.captions && els.captions.checked); }
    function buildCaptions(text) {
        captions = [];
        // stress marks (U+0301) are synthesis markup — never show them in captions
        text = (text || '').replace(/́/g, '').replace(/\s+/g, ' ').trim();
        if (!text || !durSec) return;
        var words = text.split(' '), segs = [], cur = '';
        for (var i = 0; i < words.length; i++) {
            var w = words[i];
            if (cur && (cur + ' ' + w).length > 42) { segs.push(cur); cur = w; } else cur = cur ? cur + ' ' + w : w;
        }
        if (cur) segs.push(cur);
        var total = segs.reduce(function (s, x) { return s + x.length; }, 0) || 1, tc = 0;
        for (var j = 0; j < segs.length; j++) { var d = durSec * (segs[j].length / total); var end = (j === segs.length - 1) ? durSec : tc + d; captions.push({ text: segs[j], start: tc, end: end }); tc += d; }
    }
    function captionAt(t) { for (var i = 0; i < captions.length; i++) if (t >= captions[i].start && t < captions[i].end) return captions[i].text; return ''; }
    function roundRectPath(ctx, x, y, w, h, r) { ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath(); }
    function drawCaptions(ctx, t, W, H) {
        if (!captionsOn()) return;
        var txt = captionAt(t); if (!txt) return;
        var fs = Math.max(14, Math.round(H * 0.046));
        ctx.font = '700 ' + fs + 'px system-ui, "Segoe UI", Arial, sans-serif';
        ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
        var maxW = W * 0.86, words = txt.split(' '), lines = [], cur = '';
        for (var i = 0; i < words.length; i++) { var test = cur ? cur + ' ' + words[i] : words[i]; if (ctx.measureText(test).width > maxW && cur) { lines.push(cur); cur = words[i]; } else cur = test; }
        if (cur) lines.push(cur);
        var lh = fs * 1.28, padX = fs * 0.5, padY = fs * 0.3;
        var baseY = H - Math.round(H * 0.07) - (lines.length - 1) * lh;
        for (var l = 0; l < lines.length; l++) {
            var ly = baseY + l * lh, tw = ctx.measureText(lines[l]).width;
            ctx.fillStyle = 'rgba(0,0,0,0.55)';
            roundRectPath(ctx, (W - tw) / 2 - padX, ly - fs - padY * 0.1, tw + padX * 2, fs + padY * 1.5, fs * 0.22); ctx.fill();
            ctx.lineWidth = Math.max(2, fs * 0.12); ctx.strokeStyle = 'rgba(0,0,0,0.85)'; ctx.lineJoin = 'round'; ctx.strokeText(lines[l], W / 2, ly);
            ctx.fillStyle = '#fff'; ctx.fillText(lines[l], W / 2, ly);
        }
    }

    function resizePreview() {
        if (!renderer) return;
        var sz = currentSize(), aspect = sz.w / sz.h, wrap = els.canvasWrap;
        var availW = Math.max(160, wrap.clientWidth - 2), maxH = 460;
        var w = availW, h = w / aspect;
        if (h > maxH) { h = maxH; w = h * aspect; }
        renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
        renderer.setSize(Math.round(w), Math.round(h), true);
        camera.aspect = aspect; camera.updateProjectionMatrix();
        frameCamera();
    }

    /* ───────────────────────── avatar ───────────────────────── */
    var OPEN_NAMES = ['viseme_aa', 'jawopen', 'mouthopen', 'aa', 'jaw_open', 'v_aa'];
    var WIDE_NAMES = ['viseme_i', 'viseme_e', 'mouthsmile', 'mouthsmileleft', 'mouthsmileright', 'v_ih', 'viseme_ss', 'smile'];
    var ROUND_NAMES = ['viseme_u', 'viseme_o', 'mouthfunnel', 'mouthpucker', 'v_ou', 'viseme_ou'];
    var BLINK_NAMES = ['eyeblinkleft', 'eyeblinkright', 'eyesclosed', 'blink', 'eyes_closed'];

    function collectRole(meshes, names) {
        var out = [];
        meshes.forEach(function (m) {
            var dict = m.morphTargetDictionary; if (!dict) return;
            for (var k in dict) if (names.indexOf(k.toLowerCase()) !== -1) out.push({ infl: m.morphTargetInfluences, index: dict[k] });
        });
        return out;
    }

    function disposeAvatar() {
        if (!avatarRoot) return;
        try { scene.remove(avatarRoot); } catch (_) {}
        avatarRoot.traverse(function (o) {
            if (o.geometry) { try { o.geometry.dispose(); } catch (_) {} }
            if (o.material) {
                var mats = Array.isArray(o.material) ? o.material : [o.material];
                mats.forEach(function (m) {
                    ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'emissiveMap', 'aoMap', 'alphaMap'].forEach(function (k) { if (m[k] && m[k].dispose) { try { m[k].dispose(); } catch (_) {} } });
                    try { m.dispose(); } catch (_) {}
                });
            }
        });
        avatarRoot = null; headBone = null; hasMorphs = false;
        gnmMode = false; gnmBase = null; gnmPos = null; gnmGeo = null;
        morphRoles = { open: [], wide: [], round: [], blink: [] };
    }

    function loadGnmAvatar(av) {
        var t0 = performance.now();
        setStatus(L('loading_avatar', 'Loading avatar…'));
        if (els.retry) els.retry.style.display = 'none';
        gnmLoad().then(function (d) {
            if (disposed) return;
            var face = av.fromSaved ? (gnmSavedFace() || av.face || {}) : (av.face || {});
            gnmBase = gnmBuildBase(d, face);
            gnmPos = new Float32Array(gnmBase);
            gnmGeo = new THREE.BufferGeometry();
            gnmGeo.setAttribute('position', new THREE.BufferAttribute(gnmPos, 3));
            gnmGeo.setAttribute('color', new THREE.BufferAttribute(gnmColors(d, face.pal), 3));
            gnmGeo.setIndex(new THREE.BufferAttribute(d.tris, 1));
            gnmGeo.computeVertexNormals();
            var mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.72, metalness: 0.0 });
            avatarRoot = new THREE.Mesh(gnmGeo, mat);
            avatarRoot.frustumCulled = false;
            scene.add(avatarRoot);
            gnmMode = true; hasMorphs = true;   // lip-sync path is live (gnm branch)
            window.__taGnm = { d: d, ready: true };
            gnmFacePanel(false);                // ARKit morph panel is GLB-only
            frameCamera(); setStatus(''); els.stage.classList.add('ready');
            ae('ta_avatar_load_ok', { ms: Math.round(performance.now() - t0), avatar: av.id, kind: 'gnm' });
        }).catch(function (err) {
            if (disposed) return;
            console.error('[talking-avatar] gnm avatar load failed', err);
            setStatus(L('err_avatar', 'Could not load the 3D avatar.'));
            if (els.retry) els.retry.style.display = '';
            ae('ta_avatar_load_err', { ms: Math.round(performance.now() - t0), code: 'gnm_fail', msg: (err && err.message || '').slice(0, 60) });
        });
    }

    // Show/hide the «Мимика» TAB BUTTON (its ARKit morphs only exist on the GLB avatar).
    // NB: must target the tab button, not the pane — the pane's display is owned by
    // the tab switcher and force-showing it would stack two tabs at once.
    function gnmFacePanel(show) {
        try {
            var btn = document.getElementById('tav-tab-btn-face');
            if (btn) btn.style.display = show ? '' : 'none';
        } catch (_) {}
    }

    function meshoptDecoder() {
        try { return (typeof WebAssembly !== 'undefined' && window.MeshoptDecoder) ? window.MeshoptDecoder : null; } catch (_) { return null; }
    }
    var DECODER_WAIT_MS = 3000;
    function whenDecoderReady() {
        if (meshoptDecoder() || typeof WebAssembly === 'undefined') return Promise.resolve();
        return new Promise(function (res) {
            var t0 = performance.now();
            var iv = setInterval(function () {
                if (meshoptDecoder() || performance.now() - t0 > DECODER_WAIT_MS) { clearInterval(iv); res(); }
            }, 50);
        });
    }
    // One place to build a loader: the meshopt decoder is set here, so the
    // built-in packed models AND a packed custom .glb both decode.
    function newGltfLoader() {
        var l = new THREE.GLTFLoader();
        var d = meshoptDecoder();
        if (d && typeof l.setMeshoptDecoder === 'function') { try { l.setMeshoptDecoder(d); } catch (_) {} }
        return l;
    }
    function avatarUrl(av, attempt) {
        var base = (!FORCE_PLAIN && av.packed && meshoptDecoder()) ? av.packed : av.url;
        if (attempt === 2) return base + '?retry=' + Date.now();     // bypass SW + HTTP caches
        if (attempt >= 3 && ALT_ORIGIN) return ALT_ORIGIN + base;     // the other origin
        return base;
    }
    function swActive() { try { return (navigator.serviceWorker && navigator.serviceWorker.controller) ? 1 : 0; } catch (_) { return 0; } }

    // Which mouth/eye morphs a scene exposes — pure, so a candidate model can be
    // judged BEFORE it replaces the one on stage.
    function scanMorphs(root) {
        var meshes = []; root.traverse(function (o) { if (o.isMesh && o.morphTargetDictionary) meshes.push(o); });
        return {
            meshes: meshes,
            open: collectRole(meshes, OPEN_NAMES), wide: collectRole(meshes, WIDE_NAMES),
            round: collectRole(meshes, ROUND_NAMES), blink: collectRole(meshes, BLINK_NAMES)
        };
    }

    // Put a parsed GLB on stage — shared by the built-in (network) and the custom
    // (IndexedDB / picker) lanes so both get the same strip, morph registry,
    // framing and telemetry.
    function adoptGltf(gltf, av, t0, totalBytes, extra) {
        avatarRoot = gltf.scene;
        // Per-avatar mesh strip list (e.g. the male model ships helmet/face-mask
        // accessory meshes that would cover the head).
        var strip = av.strip || [];
        if (strip.length) {
            var doomed = [];
            avatarRoot.traverse(function (o) { if (o.name && strip.indexOf(o.name) !== -1) doomed.push(o); });
            doomed.forEach(function (o) { if (o.parent) o.parent.remove(o); });
        }
        avatarRoot.traverse(function (o) {
            if (o.isMesh) { o.frustumCulled = false; if (o.material) o.material.side = THREE.FrontSide; }
            if (o.isBone && /head/i.test(o.name) && !headBone) headBone = o;
        });
        scene.add(avatarRoot);
        var roles = scanMorphs(avatarRoot);
        morphRoles.open = roles.open;
        morphRoles.wide = roles.wide;
        morphRoles.round = roles.round;
        morphRoles.blink = roles.blink;
        hasMorphs = morphRoles.open.length > 0;
        // Full name→influence registry for manual expression control (a morph name
        // can live on several meshes — face + eyes + teeth — so setting it drives all).
        morphByName = {}; morphNames = [];
        roles.meshes.forEach(function (m) {
            var dict = m.morphTargetDictionary; if (!dict) return;
            for (var k in dict) {
                var lk = k.toLowerCase();
                (morphByName[lk] = morphByName[lk] || []).push({ infl: m.morphTargetInfluences, index: dict[k] });
                if (morphNames.indexOf(k) === -1) morphNames.push(k);
            }
        });
        try {
            var all = {}; roles.meshes.forEach(function (m) { Object.keys(m.morphTargetDictionary).forEach(function (k) { all[k] = 1; }); });
            window.__taDebug = { hasMorphs: hasMorphs, avatar: av.id, custom: !!av.custom, counts: { open: morphRoles.open.length, wide: morphRoles.wide.length, round: morphRoles.round.length, blink: morphRoles.blink.length }, allMorphs: Object.keys(all) };
        } catch (_) {}
        frameCamera(); setStatus(''); els.stage.classList.add('ready');
        var okp = { ms: Math.round(performance.now() - t0), bytes: totalBytes, avatar: av.id, custom: av.custom ? 1 : 0, morphs: window.__taDebug ? window.__taDebug.counts : (hasMorphs ? 1 : 0) };
        if (extra) for (var xk in extra) okp[xk] = extra[xk];
        ae('ta_avatar_load_ok', okp);
    }

    function loadAvatar(attempt) {
        attempt = (typeof attempt === 'number' && attempt > 0) ? attempt : 1;   // the retry button passes a MouseEvent
        if (!webglOk) return;
        // Prefer the meshopt-packed model, but only if its decoder is on the page.
        // The decoder is defer+ordered before this script, so it is present at boot
        // in the normal case; this only covers the late-inject race, and only for a
        // built-in's first attempt (a stall/error retry must not re-wait).
        var pending = AVATARS[avatarIdx];
        if (attempt === 1 && !FORCE_PLAIN && pending && !pending.custom && pending.packed && !meshoptDecoder()) {
            setStatus(L('loading_avatar', 'Loading avatar…'));
            whenDecoderReady().then(function () { if (!disposed && AVATARS[avatarIdx] === pending) loadAvatarNow(attempt); });
            return;
        }
        loadAvatarNow(attempt);
    }
    function loadAvatarNow(attempt) {
        disposeAvatar();                 // clean previous model on avatar switch
        var av = AVATARS[avatarIdx];
        if (av.custom && !customAvatar) { avatarIdx = 0; av = AVATARS[0]; refreshAvatarOptions(); }
        if (av.type === 'gnm') { loadGnmAvatar(av); return; }
        gnmFacePanel(true);
        var loader = newGltfLoader();
        var t0 = performance.now(), totalBytes = 0, loadedBytes = 0, settled = false, lastProgress = t0;
        var url = av.custom ? null : avatarUrl(av, attempt);
        var packed = !!(url && av.packed && url.indexOf(av.packed) !== -1);
        setStatus(L('loading_avatar', 'Loading avatar…') + (attempt > 1 ? ' ↻' : ''));
        if (els.retry) els.retry.style.display = 'none';

        var tick = setInterval(function () {
            if (settled) return;
            var now = performance.now();
            if (now - lastProgress > AVATAR_STALL_MS) { settle(); fail('stall', null); }
            else if (now - t0 > AVATAR_HARD_CAP_MS) { settle(); fail('timeout', null); }
        }, 2000);
        function settle() { settled = true; clearInterval(tick); }
        function fail(code, err) {
            var canRetry = !av.custom && !disposed && attempt < 1 + AVATAR_RETRIES && !(attempt === 2 && !ALT_ORIGIN);
            ae('ta_avatar_load_err', {
                ms: Math.round(performance.now() - t0), code: code, avatar: av.id, attempt: attempt, packed: packed ? 1 : 0,
                loaded: loadedBytes, total: totalBytes, sw: swActive(), online: (typeof navigator !== 'undefined' && navigator.onLine === false) ? 0 : 1,
                name: (err && err.name) || '', msg: (err && err.message || '').slice(0, 60), retrying: canRetry ? 1 : 0
            });
            if (canRetry) {
                setStatus(L('loading_avatar', 'Loading avatar…') + ' ↻');
                setTimeout(function () { if (!disposed && avatarIdx === AVATARS.indexOf(av)) loadAvatar(attempt + 1); }, 1500);
                return;
            }
            setStatus(L('err_avatar', 'Could not load the 3D avatar.'));
            if (els.retry) els.retry.style.display = '';
            if (code === 'load_fail') toast(L('err_avatar', 'Could not load the 3D avatar.'), 'error');
        }
        function onOk(gltf) {
            if (settled) return; settle();
            adoptGltf(gltf, av, t0, totalBytes || loadedBytes, { attempt: attempt, packed: packed ? 1 : 0 });
        }
        function onErr(err) {
            if (settled) return; settle();
            console.error('[talking-avatar] avatar load failed', err);
            fail('load_fail', err);
        }
        function onProgress(p) {
            if (!p || settled) return;
            lastProgress = performance.now();
            if (p.loaded) loadedBytes = p.loaded;
            if (p.total) { totalBytes = p.total; setStatus(L('loading_avatar', 'Loading avatar…') + ' ' + Math.round(p.loaded / p.total * 100) + '%'); }
        }
        if (av.custom) {
            // Re-entry from the picker (a built-in was chosen, then «my avatar» again):
            // the bytes are already in memory — no network, no IndexedDB round-trip.
            totalBytes = customAvatar.bytes;
            try { loader.parse(customAvatar.buffer, '', onOk, onErr); } catch (e) { onErr(e); }
            return;
        }
        loader.load(url, onOk, onProgress, onErr);
    }

    /* ───────────────────────── custom avatar (.glb) ─────────────────────────
       Lifecycle: pick (uploader) → parse → scanMorphs gate → adopt → persist to
       IndexedDB; boot with saved avatar == 'custom' → restore from IndexedDB →
       the same adopt path (source 'restore', nothing re-written, no toast).
       Rejections put the picker back — otherwise the collapsed «file picked»
       row would show a refused file as accepted. */
    function customIndex() { for (var i = 0; i < AVATARS.length; i++) if (AVATARS[i].id === CUSTOM_ID) return i; return -1; }
    function ensureCustomEntry() {
        if (customIndex() === -1) AVATARS.push({ id: CUSTOM_ID, type: 'glb', custom: true, url: null, label: 'My avatar', i18n: 'av_custom_glb' });
    }
    function dropCustomEntry() {
        var i = customIndex(); if (i !== -1) AVATARS.splice(i, 1);
        if (avatarIdx >= AVATARS.length) avatarIdx = 0;
    }
    function refreshAvatarOptions() {
        if (!els || !els.avatar) return;
        els.avatar.innerHTML = '';
        AVATARS.forEach(function (a, i) { var o = document.createElement('option'); o.value = String(i); o.textContent = a.i18n ? L(a.i18n, a.label) : a.label; els.avatar.appendChild(o); });
        els.avatar.value = String(avatarIdx);
        if (els.avatarField) els.avatarField.style.display = (AVATARS.length > 1) ? '' : 'none';
    }
    function customDraftStore() {
        if (customStore === null) {
            try { customStore = window.__toolDraft ? window.__toolDraft.store(SLUG, { maxAgeMs: 365 * 864e5 }) : false; } catch (_) { customStore = false; }
        }
        return customStore || null;
    }
    function setCustomProbe(p) { try { window.__taCustom = p; } catch (_) {} }
    function showCustomLine() {
        if (!els.customLine) return;
        if (customAvatar) {
            if (els.customName) els.customName.textContent = L('custom_line', 'Your avatar: :name · mouth shapes: :n').replace(':name', customAvatar.name).replace(':n', String(customAvatar.visemes));
            els.customLine.style.display = '';
        } else {
            els.customLine.style.display = 'none';
        }
    }
    function parseGlb(buf) {
        return new Promise(function (res, rej) {
            try { newGltfLoader().parse(buf, '', res, rej); } catch (e) { rej(e); }
        });
    }
    function disposeParsed(root) {
        try { root.traverse(function (o) { if (o.isMesh && o.geometry) o.geometry.dispose(); }); } catch (_) {}
    }
    function restorePicker() {
        try { if (els.glbDrop && typeof els.glbDrop.__uploaderRestore === 'function') els.glbDrop.__uploaderRestore(true); } catch (_) {}
    }

    // source: 'pick' (uploader) | 'restore' (IndexedDB on boot). Resolves true once adopted.
    function adoptCustomFile(file, source) {
        var t0 = performance.now();
        if (!file || !webglOk) return Promise.resolve(false);
        setStatus(L('loading_avatar', 'Loading avatar…'));
        return file.arrayBuffer().then(function (buf) {
            return parseGlb(buf).then(function (gltf) { return { buf: buf, gltf: gltf }; });
        }).then(function (r) {
            if (disposed) return false;
            var roles = scanMorphs(r.gltf.scene);
            if (!roles.open.length) { disposeParsed(r.gltf.scene); return rejectCustom('no_visemes', file, source, t0, roles); }
            var vis = roles.open.length + roles.wide.length + roles.round.length + roles.blink.length;
            customAvatar = { name: file.name || 'avatar.glb', bytes: file.size || r.buf.byteLength, buffer: r.buf, visemes: vis };
            ensureCustomEntry();
            avatarIdx = customIndex();
            stopPreview();
            if (els.stage) els.stage.classList.remove('ready');
            disposeAvatar();
            gnmFacePanel(true);
            refreshAvatarOptions();
            adoptGltf(r.gltf, AVATARS[avatarIdx], t0, customAvatar.bytes);
            try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'avatar', CUSTOM_ID); } catch (_) {}
            var probe = { state: 'ok', name: customAvatar.name, bytes: customAvatar.bytes, source: source, stored: null,
                visemes: { open: roles.open.length, wide: roles.wide.length, round: roles.round.length, blink: roles.blink.length } };
            setCustomProbe(probe);
            if (source !== 'restore') {
                var st = customDraftStore();
                if (st) {
                    st.save('glb', { state: { name: customAvatar.name, bytes: customAvatar.bytes, v: 1 }, files: [file] })
                        .then(function (r2) { probe.stored = !!(r2 && r2.withFiles); setCustomProbe(probe); })
                        .catch(function () { probe.stored = false; setCustomProbe(probe); });
                } else { probe.stored = false; setCustomProbe(probe); }
                toast(L('custom_ok', 'Your avatar is loaded and ready to talk.'), 'success');
            }
            showCustomLine();
            ae('ta_custom_avatar', { ok: 1, src: source, bytes: customAvatar.bytes, ms: Math.round(performance.now() - t0), open: roles.open.length, wide: roles.wide.length, round: roles.round.length, blink: roles.blink.length });
            return true;
        }).catch(function (err) {
            if (disposed) return false;
            console.error('[talking-avatar] custom avatar failed', err);
            return rejectCustom('parse', file, source, t0, null, err);
        });
    }
    function rejectCustom(why, file, source, t0, roles, err) {
        var msg = why === 'no_visemes'
            ? L('err_custom_no_visemes', 'This model has no mouth shapes (visemes), so it cannot talk.')
            : L('err_custom_parse', 'Could not read this model — a .glb (glTF Binary) file is needed.');
        if (source !== 'restore') toast(msg, 'error');
        restorePicker();
        if (avatarRoot) setStatus('');       // the stage keeps whatever was there; a failed RESTORE has nothing yet → caller falls back
        setCustomProbe({ state: 'rejected', why: why, name: file && file.name, source: source, morphs: roles ? { open: roles.open.length, wide: roles.wide.length } : null });
        ae('ta_custom_avatar', { ok: 0, why: why, src: source, bytes: (file && file.size) || 0, ms: Math.round(performance.now() - t0), msg: (err && err.message || '').slice(0, 60) });
        if (source === 'restore') forgetCustomDraft();
        return false;
    }
    function forgetCustomDraft() { try { var st = customDraftStore(); if (st) st.remove('glb'); } catch (_) {} }
    function removeCustomAvatar(how) {
        if (!customAvatar) return;
        var wasActive = !!(AVATARS[avatarIdx] && AVATARS[avatarIdx].custom);
        customAvatar = null;
        forgetCustomDraft();
        dropCustomEntry();
        showCustomLine();
        setCustomProbe({ state: 'none' });
        if (how !== 'uploader') restorePicker();
        if (wasActive) {
            avatarIdx = 0;
            stopPreview();
            if (els.stage) els.stage.classList.remove('ready');
            loadAvatar();
            try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'avatar', AVATARS[0].id); } catch (_) {}
        }
        refreshAvatarOptions();
        ae('ta_custom_avatar', { ok: 1, src: 'remove', how: how });
    }
    function restoreCustomAvatar() {
        var st = customDraftStore();
        if (!st) return Promise.resolve(false);
        return st.load('glb').then(function (d) {
            if (!d || !d.files || !d.files[0]) return false;
            return adoptCustomFile(d.files[0], 'restore');
        }).catch(function () { return false; });
    }

    function frameCamera() {
        if (!avatarRoot || !camera) return;
        var box = new THREE.Box3().setFromObject(avatarRoot);
        var size = new THREE.Vector3(); box.getSize(size);
        var center = new THREE.Vector3(); box.getCenter(center);
        var fov = camera.fov * Math.PI / 180;
        // GLB avatars are FULL-BODY (frame the top 32% = the head); the GNM mesh is
        // already just a head+neck — frame the whole box or the camera lands on the
        // forehead (caught visually on the first integration pass).
        var coverH = (gnmMode ? size.y * 1.12 : size.y * 0.32) / camZoom;
        var targetY = gnmMode ? center.y : (box.max.y - coverH * 0.44);
        // A custom GLB has no known proportions (Ready Player Me exports half-body
        // AND full-body rigs, 2.5× apart in height) — when it has a head bone,
        // frame the head from the bone, not from a fraction of the box.
        if (AVATARS[avatarIdx] && AVATARS[avatarIdx].custom && headBone) {
            var hp = new THREE.Vector3(); headBone.getWorldPosition(hp);
            coverH = Math.min(size.y, Math.max(0.46, size.y * 0.25)) / camZoom;
            targetY = hp.y + coverH * 0.12;
        }
        var dist = (coverH * 0.5) / Math.tan(fov / 2) + size.z * 0.5;
        var target = new THREE.Vector3(center.x, targetY, center.z);
        camera.position.set(center.x, targetY, box.max.z + dist);
        camera.lookAt(target);
        if (controls) { controls.target.copy(target); controls.update(); }
    }

    /* ───────────────────────── lip-sync ─────────────────────────
       The whole model lives in talking-avatar/lipsync.js; this just runs it and
       reports which lane it took, because the two are visibly different quality
       and we want to see the split in telemetry rather than guess it. */
    function analyze(buffer, visemeTrack) {
        durSec = buffer.duration;
        var t0 = performance.now();
        lip = window.TalkingAvatarLipSync.build(buffer, visemeTrack || null);
        // Channel health rides along with the lane. The acoustic estimator was
        // measured on Edge audio only — the Silero voices are a different
        // vocoder and there is no ground truth for them to score against, so
        // instead of guessing, we ask production: a channel whose p95 sits near
        // zero is the exact failure the old code had (wide pinned at ~0), and
        // this is what would show it without anyone having to notice by eye.
        function p95(a) {
            if (!a || !a.length) return 0;
            var step = Math.max(1, Math.floor(a.length / 600)), s = [];
            for (var i = 0; i < a.length; i += step) s.push(a[i]);
            s.sort(function (x, y) { return x - y; });
            return +s[Math.floor(s.length * 0.95)].toFixed(3);
        }
        ae('ta_lipsync', {
            lane: lip.lane, ms: Math.round(performance.now() - t0), dur: +durSec.toFixed(1),
            events: lip.events || 0, provider: isSileroVoice(els.voice && els.voice.value) ? 'silero' : 'edge',
            p95_open: p95(lip.open), p95_wide: p95(lip.wide), p95_round: p95(lip.round)
        });
        // Inspection hooks, same idea as __taDebug above: the lip track and the
        // live morph influences are the two things that can be wrong while the
        // page looks and sounds perfectly fine, so both are readable from
        // outside (tests/playwright/talking-avatar-lipsync.mjs).
        try {
            window.__taLipProbe = { ready: true, lane: lip.lane, events: lip.events || 0, dur: durSec, open: lip.open, wide: lip.wide, round: lip.round };
        } catch (_) {}
    }
    // Hold the mouth at an arbitrary point of the voice-over, without playing it.
    // Lets a test photograph a KNOWN phoneme (a /m/ closure vs the /a/ beside it)
    // instead of hoping to catch it mid-playback.
    //
    // It must HOLD, not just render once: the rAF loop re-applies the current
    // playback state every frame and overwrote a one-shot render before the
    // screenshot was taken — both frames came out identical and looked like the
    // morphs were dead. Pass null to release.
    window.__taRenderAt = function (t) {
        if (!lip || !renderer || !scene || !camera) return false;
        holdT = (t === null || t === undefined) ? null : Math.max(0, Math.min(durSec, +t || 0));
        if (holdT !== null) { applyState(holdT, false); renderer.render(scene, camera); }
        return true;
    };
    window.__taMorphProbe = function () {
        function peak(names) {
            var best = 0;
            for (var i = 0; i < names.length; i++) {
                var arr = morphByName[names[i]];
                if (!arr) continue;
                for (var j = 0; j < arr.length; j++) best = Math.max(best, arr[j].infl[arr[j].index] || 0);
            }
            return best;
        }
        return { open: peak(OPEN_NAMES), wide: peak(WIDE_NAMES), round: peak(ROUND_NAMES) };
    };

    function sampleEnv(arr, t) {
        if (!arr || !arr.length) return 0;
        var x = t * ENV_FPS, i = Math.floor(x);
        if (i < 0) return arr[0]; if (i >= arr.length - 1) return arr[arr.length - 1];
        var f = x - i; return arr[i] * (1 - f) + arr[i + 1] * f;
    }
    // Write EVERY known morph each frame (unset name → 0) so manual values persist and
    // stale ones clear on their own. frame maps lowerName → 0..1.
    function writeMorphs(frame) {
        for (var name in morphByName) {
            var v = frame[name] || 0; if (v > 1) v = 1; else if (v < 0) v = 0;
            var arr = morphByName[name];
            for (var i = 0; i < arr.length; i++) arr[i].infl[arr[i].index] = v;
        }
    }
    function addNames(frame, names, v) {
        if (!v) return;
        for (var i = 0; i < names.length; i++) { var n = names[i]; if (morphByName[n]) frame[n] = Math.min(1, (frame[n] || 0) + v); }
    }
    function blinkCurve(t) { var cyc = 3.6, pos = (t % cyc) / cyc, bw = 0.14 / cyc; return pos > 1 - bw ? Math.sin(((pos - (1 - bw)) / bw) * Math.PI) : 0; }

    function applyState(t, idle) {
        var live = !idle && hasMorphs && lip;
        var open = live ? sampleEnv(lip.open, t) : 0;
        var wide = live ? sampleEnv(lip.wide, t) : 0;
        var round = live ? sampleEnv(lip.round, t) : 0;
        // ── GNM parametric avatar branch: same lip-sync envelope drives the mapped
        // mouth comps; blink + head sway mirror the GLB behavior (mesh-level rotation).
        if (gnmMode) {
            if (gnmBase && window.__taGnm && window.__taGnm.d) {
                gnmFrame(window.__taGnm.d, idle ? 0 : open, idle ? 0 : wide, idle ? 0 : round, idleAnim ? blinkCurve(t) : 0);
            }
            if (avatarRoot) {
                var gby = manualPose ? manualPose.ry : 0, gbx = manualPose ? manualPose.rx : 0, gbz = manualPose ? manualPose.rz : 0;
                if (idleAnim) {
                    avatarRoot.rotation.y = gby + Math.sin(t * 0.7) * 0.045 + (idle ? 0 : open * 0.02);
                    avatarRoot.rotation.x = gbx + Math.sin(t * 0.9 + 1.3) * 0.025;
                    avatarRoot.rotation.z = gbz;
                } else {
                    avatarRoot.rotation.set(gbx, gby, gbz);
                }
            }
            return;
        }
        var frame = {};
        // 1) manual base pose — the expression the user dialed in (authoritative base)
        for (var k in manualMorphs) frame[k] = manualMorphs[k];
        // 2) lip-sync mouth, ADDED on top of the manual base so a posed face can still talk
        if (live) {
            addNames(frame, OPEN_NAMES, open * 0.32);
            addNames(frame, WIDE_NAMES, wide * 0.34);
            addNames(frame, ROUND_NAMES, round * 0.28);
        }
        // 3) idle blink (suppressed when the user froze idle motion)
        if (idleAnim) addNames(frame, BLINK_NAMES, blinkCurve(t));
        writeMorphs(frame);
        // 4) head pose: manual base (if any) + gentle idle sway unless frozen
        if (headBone) {
            var bx = manualPose ? manualPose.rx : 0, by = manualPose ? manualPose.ry : 0, bz = manualPose ? manualPose.rz : 0;
            if (idleAnim) {
                headBone.rotation.y = by + Math.sin(t * 0.7) * 0.045 + (idle ? 0 : open * 0.02);
                headBone.rotation.x = bx + Math.sin(t * 0.9 + 1.3) * 0.025;
                headBone.rotation.z = bz;
            } else {
                headBone.rotation.set(bx, by, bz);
            }
        }
    }

    var holdT = null;                 // inspection hold (window.__taRenderAt), null in normal use
    function tick() {
        if (disposed) return;
        tickId = requestAnimationFrame(tick);
        if (exporting) return;
        if (renderer && renderer.getContext().isContextLost()) return;   // the frame would compile into a dead context
        if (holdT !== null) { applyState(holdT, false); if (renderer && scene && camera) renderer.render(scene, camera); return; }
        var t = 0;
        if (previewing && audioCtx) { t = waveTime(); if (_pausedAt == null && t >= durSec) { stopPreview(); t = 0; } }
        if (previewing) applyState(t, false); else applyState((performance.now() / 1000), true);
        if (previewing && els.wave) waveDraw();   // progress cursor over the waveform
        if (els.caption) { var ct = (previewing && captionsOn()) ? captionAt(t) : ''; if (els.caption.textContent !== ct) { els.caption.textContent = ct; els.caption.style.display = ct ? '' : 'none'; } }
        if (controls) controls.update();
        if (renderer && scene && camera) renderer.render(scene, camera);
    }

    /* ── splitting a long text into synthesis-sized pieces ──
       Mirrors /text-to-speech's splitChunks(): sentence boundaries first, word
       boundaries when a single sentence is longer than the wall, hard cut only
       as a last resort. Cutting mid-word would make Edge voice a fragment and
       the seam audible. */
    /* Edge's measured per-synthesis wall. 1500 for most voices; 800 for the ones
       that truncate early — mirrors TextToSpeechHandler::chunkLimitForVoice, and
       it is NOT a theoretical case here: ru-RU-DmitryNeural is on that list and
       is one of this tool's two Russian voices. Above Edge's ~900-char Read-Aloud
       cliff those voices stop sending turn.end and hand back a clip that ends
       mid-thought. It did not matter while the whole text was capped at 1500 —
       one piece, one risk; now a long text is seven pieces and seven risks. */
    function synthChunkChars(voice) {
        voice = String(voice || '');
        return (voice.indexOf('Multilingual') !== -1 || voice === 'ru-RU-DmitryNeural') ? 800 : 1500;
    }
    function splitForSynthesis(text, max) {
        text = String(text || '');
        if (text.length <= max) return [text];
        var out = [], cur = '';
        /* ⚠️ Без regex-lookbehind: старый WebKit (iOS до 16.4 — Safari, Chrome и Yandex на
           iOS живут на одном движке) не разбирает такую регулярку и роняет ВЕСЬ файл на
           этапе парсинга (Sentry HE: «invalid group specifier name»). Сентинел после
           разделителя даёт тот же результат, что split с lookbehind: разделитель остаётся у
           предыдущего куска, пробелы после него снимаются. */
        var sentences = text.replace(/([.!?。！？\n])(\s*)/g, function (m0, term, ws, off) {
            /* Точная эквивалентность split с lookbehind: пустое совпадение в САМОМ КОНЦЕ
               строки JS-split не считает точкой разреза (иначе появлялся бы хвостовой ''),
               а непустой пробельный хвост — считает. Поймано стендом эквивалентности. */
            return term + ((off + m0.length === text.length && ws === '') ? '' : '\u0001');
        }).split('\u0001');
        function pushWordwise(s) {
            var words = s.split(/\s+/), part = '';
            for (var i = 0; i < words.length; i++) {
                var w = words[i];
                if ((part + ' ' + w).trim().length > max) {
                    if (part.trim()) out.push(part.trim());
                    // a single "word" longer than the wall (a URL, a pasted blob)
                    while (w.length > max) { out.push(w.slice(0, max)); w = w.slice(max); }
                    part = w;
                } else part = part ? part + ' ' + w : w;
            }
            if (part.trim()) out.push(part.trim());
        }
        for (var si = 0; si < sentences.length; si++) {
            var s = sentences[si];
            if (!s.trim()) continue;
            if (s.length > max) { if (cur.trim()) { out.push(cur.trim()); cur = ''; } pushWordwise(s); continue; }
            if ((cur + ' ' + s).trim().length > max) { if (cur.trim()) out.push(cur.trim()); cur = s; }
            else cur = cur ? cur + ' ' + s : s;
        }
        if (cur.trim()) out.push(cur.trim());
        return out.length ? out : [text.slice(0, max)];
    }

    /** Glue decoded pieces into one timeline (playback, lip-sync and export all read this). */
    function concatBuffers(list) {
        var ctx = getAudioCtx();
        var ch = 1, sr = list[0].sampleRate, total = 0, i, c;
        for (i = 0; i < list.length; i++) { ch = Math.max(ch, list[i].numberOfChannels); total += list[i].length; }
        var out = ctx.createBuffer(ch, total, sr);
        for (c = 0; c < ch; c++) {
            var dst = out.getChannelData(c), at = 0;
            for (i = 0; i < list.length; i++) {
                var b = list[i];
                dst.set(b.getChannelData(Math.min(c, b.numberOfChannels - 1)), at);
                at += b.length;
            }
        }
        return out;
    }

    /**
     * Merge per-piece viseme tracks onto the glued timeline.
     *
     * All-or-nothing on purpose: if ANY piece came back without a track (it fell
     * to the async lane, say), the whole voice-over goes to the acoustic
     * estimator. Splicing an exact lane onto an estimated one mid-sentence looks
     * worse than either alone, and the seam is exactly where an eye is drawn.
     */
    function mergeVisemeTracks(marks) {
        if (!marks.length || !window.TalkingAvatarLipSync) return null;
        var out = [];
        for (var i = 0; i < marks.length; i++) {
            if (!marks[i]) return null;
            var ev = window.TalkingAvatarLipSync.unpack(marks[i].track);
            if (!ev.length) return null;
            for (var j = 0; j < ev.length; j++) out.push({ t: ev[j].t + marks[i].at, id: ev[j].id });
        }
        return out;
    }

    /* ───────────────────────── TTS ───────────────────────── */
    function setStatus(msg) { if (els.status) { els.status.textContent = msg || ''; els.status.style.display = msg ? '' : 'none'; } }
    function busy(on) { if (els.speak) els.speak.disabled = on; if (els.export) els.export.disabled = on || !currentBuffer; if (els.audio) els.audio.disabled = on || !currentAudioBlob; }

    async function decodeToBuffer(blob) {
        var ctx = getAudioCtx();
        var arr = await blob.arrayBuffer();
        return await new Promise(function (res, rej) {
            ctx.decodeAudioData(arr.slice(0), res, function (e) { rej(new Error('decode_fail')); });
        });
    }

    /* ── voice-over daily character quota (site-wide, tier-aware) ──
       Shown up front from window.__tierLimits.tts_limit and refreshed from the
       X-Tts-Quota-Used/Cap headers each synthesis carries. */
    function quotaPaint(used, cap) {
        if (!els.quota) return;
        if (!cap || cap <= 0) { els.quota.style.display = 'none'; return; }
        var remaining = Math.max(0, cap - (used || 0));
        var tpl = (used == null)
            ? L('quota_start', 'Voice-over today: up to :cap characters')
            : L('quota_line', 'Voice-over today: :remaining of :cap characters left');
        els.quota.textContent = tpl
            .replace(':remaining', remaining.toLocaleString())
            .replace(':cap', cap.toLocaleString());
        els.quota.style.display = '';
        els.quota.classList.toggle('warn', used != null && remaining > 0 && used / cap >= 0.8);
        els.quota.classList.toggle('out', used != null && remaining <= 0);
    }
    function quotaFromHeaders(headers) {
        try {
            if (!headers || typeof headers.get !== 'function') return;
            var used = parseInt(headers.get('X-Tts-Quota-Used') || '', 10);
            var cap = parseInt(headers.get('X-Tts-Quota-Cap') || '', 10);
            if (isFinite(used) && isFinite(cap) && cap > 0) { _quotaLive = true; quotaPaint(used, cap); }
        } catch (_) {}
    }
    function quotaInit() {
        try {
            // Blade-injected DAILY cap for the current tier (NOT the per-request
            // tts_limit) — visible before the first synthesis; headers take over.
            var cap = parseInt(window.__TA_QUOTA_CAP0, 10);
            if (isFinite(cap) && cap > 0) quotaPaint(null, cap);
        } catch (_) {}
    }
    var _quotaLive = false;   // once headers arrive, they win over the static cap
    function quotaWall() {
        var msg = L('quota_exhausted', 'Daily voice-over limit reached — try again tomorrow.');
        var cta = L('quota_pro_cta', '');
        toast(cta ? (msg + ' ' + cta) : msg, 'warning');
        ae('ta_quota_wall', {});
    }

    // Server-side Silero synth (the 29 «Новые» RU voices) — same daily quota pot.
    async function synthViaSilero(text, voice, rate, pitch, signal) {
        var resp;
        try {
            resp = await fetch('/api/tts/synthesize', {
                method: 'POST', credentials: 'same-origin', signal: signal,
                headers: { 'Content-Type': 'application/json', 'Accept': 'audio/mpeg, application/json', 'X-CSRF-TOKEN': csrfToken() },
                body: JSON.stringify({ text: text, language: 'ru', voice: voice, format: 'mp3', rate: rate || 0, pitch: pitch || 0 })
            });
        } catch (ne) { if (ne && ne.name === 'AbortError') throw ne; var e1 = new Error('network'); e1.code = 'network'; throw e1; }
        quotaFromHeaders(resp.headers);
        if (resp.status === 429) {
            var b = await resp.json().catch(function () { return {}; });
            var eQ = new Error(b.error || 'rate');
            eQ.code = (b.error === 'daily_quota') ? 'quota' : 'rate';
            throw eQ;
        }
        if (resp.status >= 500 || resp.status === 503) { var e3 = new Error('server'); e3.code = 'server'; throw e3; }
        if (!resp.ok) { var e4 = new Error('http ' + resp.status); e4.code = 'http'; e4.status = resp.status; throw e4; }
        var blob = await resp.blob();
        if (!blob || blob.size < 64) { var e5 = new Error('no_audio'); e5.code = 'no_audio'; throw e5; }
        return blob;
    }

    /* ── lip-sync envelope ──
       When we ask for visemes the sync lane answers with
         "TMBV" | uint32LE trackLen | track | mp3
       instead of a bare MP3. A body envelope, not a header: the packed track
       reaches ~2.8 KB on a full-length text and nginx's default fastcgi_buffer_size
       is 4 KB — a header that big is a 502 on the origin. Anything that is not
       the magic is a plain MP3 (async lane, or a voice with no viseme data) and
       the client estimates the mouth acoustically instead. */
    async function splitEnvelope(resp) {
        var buf = await resp.arrayBuffer();
        var u8 = new Uint8Array(buf);
        if (u8.length > 8 && u8[0] === 84 && u8[1] === 77 && u8[2] === 66 && u8[3] === 86) {   // 'TMBV'
            var len = new DataView(buf).getUint32(4, true);
            if (len >= 0 && 8 + len <= u8.length) {
                var track = new TextDecoder('utf-8').decode(u8.subarray(8, 8 + len));
                return { blob: new Blob([u8.subarray(8 + len)], { type: 'audio/mpeg' }), visemes: track };
            }
        }
        return { blob: new Blob([u8], { type: 'audio/mpeg' }), visemes: null };
    }

    async function synthViaServer(text, voice, rate, pitch, signal, onProgress) {
        if (isSileroVoice(voice)) return { blob: await synthViaSilero(text, voice, rate, pitch, signal), visemes: null };
        if (!TTS_URL) { var e0 = new Error('tts url missing'); e0.code = 'config'; throw e0; }
        var resp;
        try {
            resp = await fetch(TTS_URL, {
                method: 'POST', credentials: 'same-origin', signal: signal,
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken(), 'X-Requested-With': 'XMLHttpRequest', 'Accept': '*/*' },
                body: JSON.stringify({ text: text, voice: voice, rate: rate || 0, pitch: pitch || 0, style: '', format: TTS_FMT, visemes: true })
            });
        } catch (ne) { if (ne && ne.name === 'AbortError') throw ne; var e1 = new Error('network'); e1.code = 'network'; throw e1; }
        quotaFromHeaders(resp.headers);
        // Async lane: the audio comes back reshaped (lead trim + pause splicing),
        // so the server deliberately publishes no viseme track for it — offsets
        // that no longer match the audio would animate a confidently wrong mouth.
        if (resp.status === 202) return { blob: await pollTts(await resp.json(), signal, onProgress), visemes: null };
        if (resp.status === 429) { var e2 = new Error('rate'); e2.code = 'rate'; throw e2; }
        if (resp.status >= 500) { var e3 = new Error('server'); e3.code = 'server'; throw e3; }
        if (!resp.ok) {
            var eb = await resp.json().catch(function () { return {}; });
            var kind = (eb.error_kind || '') + ' ' + (eb.error || '');
            var e4 = new Error(eb.error || eb.error_kind || ('http ' + resp.status));
            e4.status = resp.status;
            e4.suggestedVoice = eb.suggested_voice || '';
            e4.code = /quota/.test(kind) ? 'quota' : (/voice_unhealthy|voice_unstable|script_mismatch/.test(kind) ? 'voice' : (/rate/.test(kind) ? 'rate' : 'http'));
            throw e4;
        }
        var out = await splitEnvelope(resp);
        if (!out.blob || out.blob.size < 64) { var e5 = new Error('no_audio'); e5.code = 'no_audio'; throw e5; }
        return out;
    }

    async function pollTts(meta, signal, onProgress) {
        var statusUrl = meta && meta.status_url, resultUrl = meta && meta.result_url;
        if (!statusUrl) { var e = new Error('server'); e.code = 'server'; throw e; }
        var deadline = Date.now() + 180000, delays = [700, 1000, 1500, 2000], i = 0;
        while (Date.now() < deadline) {
            if (signal && signal.aborted) { var ab = new Error('aborted'); ab.name = 'AbortError'; throw ab; }
            await new Promise(function (r) { setTimeout(r, delays[Math.min(i++, delays.length - 1)]); });
            var pr;
            try { pr = await fetch(statusUrl, { headers: { 'Accept': 'application/json' }, credentials: 'same-origin', signal: signal }); }
            catch (ne) { if (ne && ne.name === 'AbortError') throw ne; continue; }
            if (pr.status === 429) { var er = new Error('rate'); er.code = 'rate'; throw er; }
            if (!pr.ok) continue;
            var d = await pr.json().catch(function () { return {}; });
            if (d.status === 'done') {
                var rr = await fetch(d.result_url || resultUrl, { credentials: 'same-origin', signal: signal });
                if (!rr.ok) { var e6 = new Error('server'); e6.code = 'server'; throw e6; }
                return await rr.blob();
            }
            if (d.status === 'error') { var e7 = new Error('server'); e7.code = 'server'; throw e7; }
            if (onProgress) onProgress(0.5);
        }
        var et = new Error('server'); et.code = 'timeout'; throw et;
    }

    function ttsErrMessage(code) {
        switch (code) {
            case 'rate': return L('err_rate', 'Too many requests right now — please wait a minute and try again.');
            case 'server': case 'timeout': return L('err_server', 'The voice service is busy. Please try again in a moment.');
            case 'network': return L('err_network', 'Network problem — check your connection and try again.');
            case 'no_audio': return L('err_no_audio', 'No audio came back. Try different text or another voice.');
            case 'voice': return L('err_voice', 'This voice is temporarily unavailable. Please pick another voice.');
            default: return L('err_tts', 'Could not generate the voice. Please try again.');
        }
    }

    /* ── voice-over strip: waveform + play/pause + seek ── */
    function waveShow(on) { if (els.wavecard) els.wavecard.classList.toggle('on', !!on); }
    function waveDraw() {
        var cv = els.wave;
        if (!cv || !currentBuffer) return;
        var w = cv.clientWidth || 300, h = cv.height;
        if (cv.width !== w) cv.width = w;
        var g = cv.getContext('2d');
        g.clearRect(0, 0, w, h);
        var ch = currentBuffer.getChannelData(0), step = Math.max(1, Math.floor(ch.length / w));
        var mid = h / 2;
        var progX = durSec ? Math.round(waveTime() / durSec * w) : 0;
        for (var x = 0; x < w; x++) {
            var mn = 1, mx = -1, base = x * step, end = Math.min(ch.length, base + step);
            for (var i = base; i < end; i += 4) { var v = ch[i]; if (v < mn) mn = v; if (v > mx) mx = v; }
            var y1 = mid + mn * mid * 0.92, y2 = mid + mx * mid * 0.92;
            g.fillStyle = x <= progX ? 'rgba(56,132,248,0.95)' : 'rgba(127,158,199,0.42)';
            g.fillRect(x, y1, 1, Math.max(1, y2 - y1));
        }
    }
    // Pause is self-contained: we PIN the position at pause-time and rebase
    // previewStart on resume. (Do not trust ctx.currentTime to freeze during
    // suspend — headless/fake-audio clocks keep ticking; the pinned value makes
    // pause exact everywhere and the lip-sync clock freezes with it.)
    var _waveDoneT = 0, _pausedAt = null;
    function waveTime() {
        if (_pausedAt != null) return _pausedAt;
        if (!previewing || !audioCtx) return _waveDoneT || 0;
        return Math.min(durSec, Math.max(0, audioCtx.currentTime - previewStart));
    }
    function ppPaint() {
        if (!els.playpause) return;
        var playing = previewing && _pausedAt == null && audioCtx && audioCtx.state === 'running';
        els.playpause.textContent = playing ? '⏸' : '▶';
        els.playpause.setAttribute('aria-label', playing ? L('btn_pause', 'Pause') : L('btn_play', 'Play'));
    }
    function togglePlayPause() {
        if (!currentBuffer) return;
        var ctx;
        try { ctx = getAudioCtx(); } catch (_) { return; }
        if (previewing && _pausedAt == null) {
            _pausedAt = waveTimeRaw();
            ctx.suspend().then(ppPaint, function () {});
            ae('ta_pause', {});
        } else if (previewing) {
            previewStart = ctx.currentTime - _pausedAt;
            _pausedAt = null;
            ctx.resume().then(ppPaint, function () {});
            ae('ta_resume', {});
        } else {
            playPreview();
        }
        ppPaint();
    }
    function waveTimeRaw() {
        if (!previewing || !audioCtx) return 0;
        return Math.min(durSec, Math.max(0, audioCtx.currentTime - previewStart));
    }
    function waveSeek(ev) {
        if (!currentBuffer || !durSec || !els.wave) return;
        var r = els.wave.getBoundingClientRect();
        var frac = Math.min(1, Math.max(0, (ev.clientX - r.left) / r.width));
        var offset = frac * durSec;
        var ctx;
        try { ctx = getAudioCtx(); } catch (_) { return; }
        stopPreview();
        if (ctx.state === 'suspended') { try { ctx.resume(); } catch (_) {} }
        previewSrc = ctx.createBufferSource();
        previewSrc.buffer = currentBuffer;
        previewSrc.connect(ctx.destination);
        previewStart = ctx.currentTime - offset;
        previewing = true;
        previewSrc.onended = function () { if (previewing) stopPreview(); };
        try { previewSrc.start(0, offset); } catch (_) {}
        if (els.stage) els.stage.classList.add('speaking');
        ppPaint();
        ae('ta_seek', { at: +offset.toFixed(1) });
    }

    /* ── Russian stress (ударения) — for the «Новые» (Silero) voices only ──
       The Silero daemon honours manual U+0301/'+' marks (a user mark is law) and
       auto-stresses everything else with the same 535k dictionary server-side;
       the client-side pass exists so the USER can SEE and fix the ambiguous
       words (homographs: за́мок/замо́к) before synthesis. Edge voices mangle the
       marks (measured — TextToSpeechHandler::preprocessForSSML strips them), so
       for Edge we show a one-tap bridge to the «Новые» voices instead. Mirrors
       /text-to-speech (same dictionary module, same flywheel telemetry). */
    var STRESS_CHAR = '́';
    var homoUnresolved = 0, homoBypass = false;

    function textIsRussian() { return /[а-яё]/i.test((els.text && els.text.value) || ''); }
    function stressCapable() { return !!(els.lang && els.lang.value === 'ru' && isSileroVoice(els.voice && els.voice.value)); }
    function onTextEdited() { try { els.text.dispatchEvent(new Event('input', { bubbles: true })); } catch (_) {} }
    function escHtml(s) { return String(s).replace(/[&<>]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]; }); }

    function hideHomoPanel() { if (els.homo) els.homo.classList.remove('on', 'resolved'); homoUnresolved = 0; }

    function refreshStressUI() {
        if (!els.stressRow) return;
        var ru = textIsRussian(), can = stressCapable();
        els.stressRow.classList.toggle('on', ru && can);
        // Bridge (discoverability): russian text + a voice that can't read marks +
        // the Silero voices actually available on this install.
        if (els.stressBridge) els.stressBridge.classList.toggle('on', ru && !can && SILERO.length > 0);
        if (!(ru && can)) hideHomoPanel();
        updateStressBtnMode();
    }

    // Context suggestions for the text they were computed for: {text, picks} | null.
    // Same server model Silero voices with — see RussianAccent.contextPicks.
    var homoPicks = null;

    // Rebuild the homograph review from the current textarea. Returns unresolved count.
    function scanHomographs() {
        if (!els.homo || !window.RussianAccent || !RussianAccent.isReady()) return 0;
        var val = els.text.value;
        var WORD = /[А-Яа-яЁё́]+/g, items = [], m;
        while ((m = WORD.exec(val)) !== null) {
            var w = m[0];
            if (w.indexOf(STRESS_CHAR) !== -1) continue;          // already stressed → resolved
            var r = RussianAccent.lookup(w);
            if (r && r.type === 'homograph' && r.forms && r.forms.length > 1) {
                items.push({ word: w, start: m.index, end: m.index + w.length, forms: r.forms });
            }
        }
        homoUnresolved = items.length;
        els.homoList.innerHTML = '';
        if (!items.length) { els.homo.classList.remove('on'); return 0; }
        var picks = (homoPicks && homoPicks.text === val) ? homoPicks.picks : null;
        if (!picks && RussianAccent.contextPicks) {
            RussianAccent.contextPicks(val).then(function (p) {
                if (!p.length || els.text.value !== val) return;
                homoPicks = { text: val, picks: p };
                scanHomographs();
            });
        }
        if (picks && picks.length) {
            var note = document.createElement('div'); note.className = 'tav-homo-note';
            note.textContent = L('homo_context_hint', '');
            if (note.textContent) els.homoList.appendChild(note);
        }
        items.forEach(function (it) {
            var row = document.createElement('div'); row.className = 'tav-homo-row';
            var ctx = document.createElement('span'); ctx.className = 'tav-homo-ctx';
            var before = val.slice(Math.max(0, it.start - 16), it.start), after = val.slice(it.end, it.end + 16);
            ctx.innerHTML = (it.start > 0 ? '…' : '') + escHtml(before) + '<b>' + escHtml(it.word) + '</b>' + escHtml(after) + (it.end < val.length ? '…' : '');
            var opts = document.createElement('span'); opts.className = 'tav-homo-opts';
            var sug = picks ? RussianAccent.pickAt(picks, it.start) : null;
            (sug ? RussianAccent.withSuggestion(it.forms, sug) : it.forms).forEach(function (f, fi) {
                var b = document.createElement('button'); b.type = 'button'; b.className = 'tav-homo-opt';
                if (sug && fi === 0) { b.classList.add('suggested'); b.title = L('homo_context_hint', ''); }
                b.textContent = RussianAccent._applyCase(it.word, f);
                b.addEventListener('click', function () {
                    var cur = els.text.value;
                    els.text.value = cur.slice(0, it.start) + RussianAccent._applyCase(it.word, f) + cur.slice(it.end);
                    // Data flywheel: same homo_choice signal as /text-to-speech —
                    // harvested weekly by tts:stress-harvest (slug whitelisted there).
                    // Deliberately only a ±16-char window, never the full text.
                    try {
                        if (window.__ae) window.__ae('feature_use', {
                            slug: SLUG, f: 'homo_choice',
                            w: it.word.toLowerCase().slice(0, 40),
                            pick: String(f).slice(0, 44),
                            ctx: (before + '[' + it.word + ']' + after).slice(0, 80),
                            sug: sug ? (RussianAccent.sameReading(f, sug) ? 1 : 0) : undefined
                        });
                    } catch (_) {}
                    onTextEdited();
                    scanHomographs();
                });
                opts.appendChild(b);
            });
            row.appendChild(ctx); row.appendChild(opts);
            els.homoList.appendChild(row);
        });
        els.homoCount.textContent = String(items.length);
        els.homo.classList.add('on'); els.homo.classList.remove('resolved');
        return items.length;
    }

    // One click: dictionary-stress every unambiguous word, list homographs for review.
    async function autoStress() {
        if (!window.RussianAccent || !els.text) return;
        homoBypass = false;   // fresh run re-arms the pre-synthesis check
        if (els.autoStress) els.autoStress.disabled = true;
        try {
            if (!RussianAccent.isReady()) await RussianAccent.load({ base: '/vendor/ru-accent' });
            if (RussianAccent.isReady()) {
                els.text.value = RussianAccent.accentuate(els.text.value, { homograph: 'leave', skipMarked: true });
                onTextEdited();
                scanHomographs();
            }
        } catch (_) {}
        if (els.autoStress) els.autoStress.disabled = false;
        els.text.focus();
        ae('ta_autostress', { homo: homoUnresolved });
    }

    /* manual U+0301 toggle at the caret — same behaviour as /text-to-speech */
    function stressAdjacent() {
        var pos = els.text.selectionStart, val = els.text.value;
        if (val[pos - 1] === STRESS_CHAR) return pos - 1;
        if (val[pos] === STRESS_CHAR) return pos;
        return -1;
    }
    function updateStressBtnMode() {
        if (!els.stressBtn) return;
        var focused = document.activeElement === els.text;
        var adj = focused ? stressAdjacent() : -1;
        els.stressBtn.title = (adj >= 0 ? els.stressBtn.dataset.removeTitle : els.stressBtn.dataset.insertTitle) || '';
        if (els.stressClear) els.stressClear.style.display = (els.text.value.indexOf(STRESS_CHAR) !== -1 && els.stressRow && els.stressRow.classList.contains('on')) ? '' : 'none';
    }
    function manualStress() {
        var val = els.text.value, pos = els.text.selectionStart;
        var adj = stressAdjacent();
        if (adj >= 0) {
            els.text.value = val.slice(0, adj) + val.slice(adj + 1);
            var np = adj < pos ? pos - 1 : pos;
            els.text.selectionStart = els.text.selectionEnd = np;
        } else {
            if (!val.length) { els.text.focus(); return; }
            els.text.value = val.slice(0, pos) + STRESS_CHAR + val.slice(pos);
            els.text.selectionStart = els.text.selectionEnd = pos + 1;
            // Flywheel: a MANUAL stress on a dictionary-miss word (names, brands,
            // neologisms) is the only source of NEW dictionary entries; known
            // words are skipped — the homograph panel is their clean channel.
            try {
                if (window.RussianAccent && RussianAccent.isReady() && window.__ae) {
                    var nv = els.text.value, ws = pos, we = pos + 1;
                    while (ws > 0 && /[А-Яа-яЁё́]/.test(nv[ws - 1])) ws--;
                    while (we < nv.length && /[А-Яа-яЁё́]/.test(nv[we])) we++;
                    var marked = nv.slice(ws, we), plain = marked.replace(/́/g, '');
                    if (plain.length >= 3 && RussianAccent.lookup(plain).type === 'miss') {
                        window.__ae('feature_use', { slug: SLUG, f: 'stress_manual', w: marked.toLowerCase().slice(0, 44), ctx: nv.slice(Math.max(0, ws - 16), Math.min(nv.length, we + 16)).slice(0, 80) });
                    }
                }
            } catch (_) {}
        }
        els.text.focus();
        onTextEdited();
        updateStressBtnMode();
    }
    function clearStress() {
        var val = els.text.value;
        if (val.indexOf(STRESS_CHAR) === -1) return;
        var a = els.text.selectionStart, b = els.text.selectionEnd;
        var hasSel = a !== b && val.slice(a, b).indexOf(STRESS_CHAR) !== -1;
        if (hasSel) {
            var cleaned = val.slice(a, b).replace(/́/g, '');
            els.text.value = val.slice(0, a) + cleaned + val.slice(b);
            els.text.selectionStart = a; els.text.selectionEnd = a + cleaned.length;
        } else {
            var idx = val.lastIndexOf(STRESS_CHAR);
            els.text.value = val.slice(0, idx) + val.slice(idx + 1);
            var cur = els.text.selectionStart, nc = idx < cur ? cur - 1 : cur;
            els.text.selectionStart = els.text.selectionEnd = nc;
        }
        els.text.focus();
        onTextEdited();
        updateStressBtnMode();
    }

    // One-tap route from a mark-mangling voice to the first «Новые» (Silero) voice.
    function bridgeToSilero() {
        if (!SILERO.length) return;
        if (els.lang.value !== 'ru') {
            els.lang.value = 'ru';
            try { els.lang.dispatchEvent(new Event('change', { bubbles: true })); } catch (_) {}
        }
        els.voice.value = SILERO[0].id;
        try { els.voice.dispatchEvent(new Event('change', { bubbles: true })); } catch (_) {}
        refreshStressUI();
        ae('ta_stress_bridge', {});
        els.text.focus();
    }

    async function onSpeak() {
        if (speakInProgress || exporting) return;
        if (!webglOk) return;
        var text = (els.text.value || '').trim();
        if (!text) { els.text.focus(); return; }
        if (text.length > MAX_CHARS) { text = text.slice(0, MAX_CHARS); toast(L('trimmed', 'Text was trimmed to ' + MAX_CHARS + ' characters.'), 'warning'); }
        if (!TTS_URL) { toast(L('err_tts', 'Voice synthesis is unavailable right now.'), 'error'); return; }
        // Obligatory stress-check step (как в /text-to-speech): unresolved homographs
        // warn once and point at the review panel; a second click proceeds with the
        // engine's own guess. The bypass re-arms on any text edit.
        if (homoUnresolved > 0 && stressCapable() && !homoBypass) {
            homoBypass = true;
            if (els.homo && els.homo.classList.contains('on')) els.homo.scrollIntoView({ block: 'nearest' });
            toast(L('homo_warn', 'Check the stress: :n homograph(s) not chosen').replace(':n', String(homoUnresolved)), 'warning');
            return;
        }

        try { getAudioCtx(); } catch (_) {} // unlock audio inside the click gesture (iOS)
        speakInProgress = true;
        stopPreview();
        busy(true);
        setStatus(L('synth', 'Generating voice…'));
        var t0 = performance.now();
        ttsAbort = (typeof AbortController === 'function') ? new AbortController() : null;
        // Stress marks (U+0301) flow through AS TYPED: the Silero daemon's accentizer
        // honours them (manual mark is law, services/silero-tts/accent.py) and the
        // Edge path strips them server-side (Edge MANGLES marks — measured, see
        // TextToSpeechHandler::preprocessForSSML). No client-side stripping needed.
        var voice = els.voice.value;
        ae('ta_speak_start', { chars: text.length, voice: voice, lang: els.lang.value });
        try {
            var rate = parseInt(els.speed.value, 10) || 0, pitch = parseInt(els.pitch && els.pitch.value, 10) || 0;
            var onP = function () { setStatus(L('synth', 'Generating voice…')); };
            // One synthesis per piece. The pieces exist because ~1500 chars is
            // Edge's own wall, not a policy: above it, it stops sending turn.end
            // and returns truncated audio (measured, TextToSpeechHandler:187).
            var pieces = splitForSynthesis(text, synthChunkChars(voice));
            var blobs = [], buffers = [], marks = [], tOff = 0;
            for (var pi = 0; pi < pieces.length; pi++) {
                if (pieces.length > 1) setStatus(L('synth', 'Generating voice…') + ' ' + (pi + 1) + '/' + pieces.length);
                var synth;
                try {
                    synth = await synthViaServer(pieces[pi], voice, rate, pitch, ttsAbort ? ttsAbort.signal : null, onP);
                } catch (eVoice) {
                    // Server gated this voice (voice_unhealthy/unstable) → retry once with the
                    // server-suggested healthy voice, or the other voice in the same language.
                    if (eVoice && eVoice.code === 'voice' && eVoice.name !== 'AbortError') {
                        var alt = eVoice.suggestedVoice || altVoice(voice);
                        if (alt && alt !== voice) {
                            voice = alt;
                            if (els.voice) { var okv = Array.prototype.some.call(els.voice.options, function (o) { return o.value === alt; }); if (okv) els.voice.value = alt; }
                            toast(L('voice_switched', 'That voice is busy right now — switched to another voice.'), 'warning');
                            ae('ta_voice_fallback', { to: alt });
                            synth = await synthViaServer(pieces[pi], voice, rate, pitch, ttsAbort ? ttsAbort.signal : null, onP);
                        } else { throw eVoice; }
                    } else { throw eVoice; }
                }
                var pbuf = await decodeToBuffer(synth.blob);
                blobs.push(synth.blob); buffers.push(pbuf);
                // Each piece's viseme offsets restart at zero. Shift by the DECODED
                // duration of everything already queued — not by a nominal estimate:
                // Edge pads every piece with its own lead/tail silence, so only the
                // real decoded length keeps the lips on the sound at the seams.
                marks.push(synth.visemes ? { track: synth.visemes, at: tOff } : null);
                tOff += pbuf.duration;
            }
            // MP3 frames are self-contained, so the download is a plain concat.
            var blob = blobs.length === 1 ? blobs[0] : new Blob(blobs, { type: 'audio/mpeg' });
            currentAudioBlob = blob;
            currentBuffer = buffers.length === 1 ? buffers[0] : concatBuffers(buffers);
            currentTakeKey = takeKeyOf([voice, rate, pitch, text]);
            analyze(currentBuffer, mergeVisemeTracks(marks));
            buildCaptions(text);
            setStatus(''); busy(false);
            waveShow(true); waveDraw();
            ae('ta_speak_ok', { ms: Math.round(performance.now() - t0), chars: text.length, dur: +durSec.toFixed(1), voice: voice, lang: els.lang.value, provider: isSileroVoice(voice) ? 'silero' : 'edge-tts', bytes: blob.size });
            playPreview();
        } catch (err) {
            currentBuffer = null; currentAudioBlob = null; currentTakeKey = null;
            busy(false); setStatus('');
            if (els.replay) els.replay.disabled = true;
            if (err && err.name === 'AbortError') { ae('ta_speak_cancel', { ms: Math.round(performance.now() - t0) }); return; }
            var code = (err && err.code) || 'tts';
            console.error('[talking-avatar] tts failed', err);
            if (code === 'quota') quotaWall();
            else toast(ttsErrMessage(code), code === 'rate' ? 'warning' : 'error');
            ae('ta_speak_err', { ms: Math.round(performance.now() - t0), code: code, status: (err && err.status) || 0 });
        } finally { speakInProgress = false; }
    }

    /* ───────────────────────── preview ───────────────────────── */
    function playPreview() {
        if (!currentBuffer) return;
        stopPreview();
        var ctx;
        try { ctx = getAudioCtx(); } catch (_) { return; }
        previewSrc = ctx.createBufferSource();
        previewSrc.buffer = currentBuffer;
        previewSrc.connect(ctx.destination);
        previewStart = ctx.currentTime;
        previewing = true;
        previewSrc.onended = function () { if (previewing) stopPreview(); };
        try { previewSrc.start(); } catch (_) {}
        if (els.stage) els.stage.classList.add('speaking');
        if (els.replay) els.replay.disabled = false;
        _waveDoneT = 0; _pausedAt = null; ppPaint();
    }
    function stopPreview() {
        _waveDoneT = previewing ? durSec : _waveDoneT;
        _pausedAt = null;
        previewing = false;
        if (previewSrc) { try { previewSrc.onended = null; previewSrc.stop(); } catch (_) {} previewSrc = null; }
        if (els.stage) els.stage.classList.remove('speaking');
        ppPaint();
    }

    /* ───────────────────────── export ───────────────────────── */
    function setProgress(frac, show, etaMs) {
        if (!els.progWrap) return;
        if (typeof show === 'boolean') els.progWrap.style.display = show ? '' : 'none';
        if (els.progBar) els.progBar.style.width = Math.round((frac || 0) * 100) + '%';
        if (els.progPct) {
            var pct = Math.round((frac || 0) * 100) + '%';
            if (etaMs && etaMs > 1500) { var s = Math.ceil(etaMs / 1000); var mm = Math.floor(s / 60), ss = s % 60; pct += ' · ' + (mm ? mm + 'm ' : '') + ss + 's'; }
            els.progPct.textContent = pct;
        }
    }

    function pickRecorderMime() {
        if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return null;
        var cands = ['video/mp4;codecs=avc1.640028,mp4a.40.2', 'video/mp4;codecs=h264,aac', 'video/mp4', 'video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'];
        for (var i = 0; i < cands.length; i++) if (MediaRecorder.isTypeSupported(cands[i])) return cands[i];
        return null;
    }

    async function exportMediaRecorder(sz, signal) {
        var mime = pickRecorderMime();
        if (!mime) throw new Error('no_recorder');
        // The first video recorder of a Chromium tab drops its first ~2 s of
        // frames: the face would stand still while the voice runs on.
        if (window.__mediaRecorderWarm) await window.__mediaRecorderWarm({ width: sz.w, height: sz.h, mimeType: mime, videoBitsPerSecond: sz.w * sz.h * 4 });
        var ctx = getAudioCtx();
        var dest = ctx.createMediaStreamDestination();
        var src = ctx.createBufferSource(); src.buffer = currentBuffer; src.connect(dest);
        // Composite (WebGL frame + captions) into a 2D canvas and capture THAT, so
        // burned-in captions are included in the realtime recording too.
        var comp = document.createElement('canvas'); comp.width = sz.w; comp.height = sz.h;
        var cctx = comp.getContext('2d');
        var vstream = comp.captureStream(30);
        var stream = new MediaStream();
        vstream.getVideoTracks().forEach(function (t) { stream.addTrack(t); });
        dest.stream.getAudioTracks().forEach(function (t) { stream.addTrack(t); });
        var rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: sz.w * sz.h * 4 });
        var chunks = [];
        rec.ondataavailable = function (e) { if (e.data && e.data.size) chunks.push(e.data); };
        var stopped = new Promise(function (res) { rec.onstop = res; });
        var t0 = performance.now();
        rec.start(); src.start();
        await new Promise(function (resolve) {
            function step() {
                if (disposed || (signal && signal.aborted)) { resolve(); return; }
                var elapsed = (performance.now() - t0) / 1000;
                applyState(Math.min(elapsed, durSec), false);
                renderer.render(scene, camera);
                cctx.drawImage(renderer.domElement, 0, 0, sz.w, sz.h);
                drawCaptions(cctx, Math.min(elapsed, durSec), sz.w, sz.h);
                setProgress(Math.min(1, elapsed / durSec), true, (durSec - elapsed) * 1000);
                if (elapsed < durSec + 0.2) requestAnimationFrame(step); else resolve();
            }
            requestAnimationFrame(step);
        });
        try { src.stop(); } catch (_) {}
        try { rec.stop(); } catch (_) {}
        await stopped;
        if (signal && signal.aborted) { var ab = new Error('aborted'); ab.name = 'AbortError'; throw ab; }
        return new Blob(chunks, { type: (mime.split(';')[0]) });
    }

    async function onExport() {
        if (!currentBuffer || exporting || !webglOk) return;
        stopPreview();
        var sz = currentSize();
        var fmt = (els.format && els.format.value) || 'mp4';
        var fps = 30;
        var useWebCodecs = false, support = null;
        if (window.CanvasVideoExport) {
            support = await window.CanvasVideoExport.isSupported({ width: sz.w, height: sz.h, fps: fps, format: fmt, audioSampleRate: currentBuffer.sampleRate, audioChannels: 1 }).catch(function () { return { ok: false }; });
            if (support && support.ok) useWebCodecs = true;
            else if (fmt === 'mp4') {
                var alt = await window.CanvasVideoExport.isSupported({ width: sz.w, height: sz.h, fps: fps, format: 'webm', audioSampleRate: currentBuffer.sampleRate, audioChannels: 1 }).catch(function () { return { ok: false }; });
                if (alt && alt.ok) { fmt = 'webm'; useWebCodecs = true; support = alt; ae('ta_export_format_fallback', { from: 'mp4', to: 'webm' }); toast(L('mp4_to_webm', 'MP4 isn’t available here — exporting WebM instead.'), 'warning'); }
            }
        }
        var recorderMime = pickRecorderMime();
        if (!useWebCodecs && !recorderMime) { toast(L('err_export', 'Video export is not supported in this browser.'), 'error'); ae('ta_export_err', { code: 'unsupported' }); return; }

        /* Soft daily cap. Gated here: below the "export unsupported" bail (a browser
           that cannot export must not burn a run) and above the UI lock, so hitting
           the wall cannot leave the controls disabled. commit() once the encoder has
           produced a blob — the render is the work; the file save that follows is not
           billed, and neither is the audio-only download (onAudioDownload) nor the
           TTS preview. abort() in the finally covers the catch, including the
           AbortError raised by the Cancel button.
           NB begin() sits after two awaits (CanvasVideoExport.isSupported probes), so
           a test reading a begin-spy on a fixed delay will see 0 — read it after the
           run settles.
           The unit is the spoken TAKE, keyed explicitly. With no key, begin()
           falls back to the page's uploader selection, and the only uploader here
           is the OPTIONAL custom avatar (.glb): once someone had loaded their own
           avatar, every later export that day counted as a free repeat of that
           file and the cap stopped existing (audit 2026-09-25). Re-exporting the
           same take in another size or format stays free; a new take costs one. */
        var myRun = window.__toolLimit ? window.__toolLimit.begin('talking-avatar', currentTakeKey || []) : null;
        if (window.__toolLimit && !myRun) return;   // wall hit — note already shown

        exporting = true;
        exportAbort = (typeof AbortController === 'function') ? new AbortController() : null;
        if (els.export) els.export.disabled = true;
        if (els.speak) els.speak.disabled = true;
        if (els.cancel) els.cancel.style.display = '';
        [els.lang, els.voice, els.size, els.format, els.audio].forEach(function (e) { if (e) e.disabled = true; });
        setProgress(0, true);
        var t0 = performance.now();

        // Save renderer size/pixelRatio for restore (background is re-derived from
        // the UI via applyBackground(), which handles both solid + gradient).
        var prevSize = new THREE.Vector2(); renderer.getSize(prevSize);
        var prevPR = renderer.getPixelRatio();
        renderer.setPixelRatio(1);
        renderer.setSize(sz.w, sz.h, false);
        camera.aspect = sz.w / sz.h; camera.updateProjectionMatrix(); frameCamera();
        applyBackground();

        var path = useWebCodecs ? 'webcodecs' : 'mediarecorder';
        ae('ta_export_start', { fmt: fmt, dur: +durSec.toFixed(1), w: sz.w, h: sz.h, path: path, hardware: !!(support && support.hardware) });
        var tail = durSec > 0.5 ? 0.25 : 0;
        try {
            var blob;
            if (useWebCodecs) {
                blob = await window.CanvasVideoExport.encode({
                    width: sz.w, height: sz.h, fps: fps, durationSec: Math.max(durSec + tail, 0.2),
                    format: fmt, qualityCrf: 23, audioBuffer: currentBuffer, signal: exportAbort ? exportAbort.signal : null,
                    drawFrame: function (ctx, frameIndex, timeSec) { var tt = Math.min(timeSec, durSec); applyState(tt, false); renderer.render(scene, camera); ctx.drawImage(renderer.domElement, 0, 0, sz.w, sz.h); drawCaptions(ctx, tt, sz.w, sz.h); },
                    onProgress: function (f) { var el = (performance.now() - t0); setProgress(f, true, f > 0.02 ? (el / f - el) : 0); }
                });
            } else {
                blob = await exportMediaRecorder(sz, exportAbort ? exportAbort.signal : null);
                if (blob.type.indexOf('webm') !== -1) fmt = 'webm'; else if (blob.type.indexOf('mp4') !== -1) fmt = 'mp4';
            }
            var name = 'talking-avatar-' + Date.now() + '.' + fmt;
            if (myRun) myRun.commit();   // encoder produced the video → bill one run (not the save)
            // AI Act art. 50(2): MP4 gets a machine-readable AI-note (moov udta
            // comment) at the download boundary; WebM passes through (visible
            // on-page notice covers it) and any marking failure falls back to
            // the raw blob — never blocks the download.
            var sendVid = function (b) {
                (window.__downloadFile || function (x, n) { var u = URL.createObjectURL(x); var a = document.createElement('a'); a.href = u; a.download = n; a.click(); URL.revokeObjectURL(u); })(b || blob, name);
            };
            if (window.__aiMark) { window.__aiMark.auto(blob, name, 'talking-avatar').then(sendVid, function () { sendVid(blob); }); }
            else { sendVid(blob); }
            toast(L('export_ok', 'Video ready — downloading.'), 'success');
            ae('ta_export_ok', { ms: Math.round(performance.now() - t0), fmt: fmt, dur: +durSec.toFixed(1), w: sz.w, h: sz.h, bytes: blob.size, path: path });
        } catch (err) {
            if (err && err.name === 'AbortError') ae('ta_export_cancel', { ms: Math.round(performance.now() - t0) });
            else { console.error('[talking-avatar] export failed', err); toast(L('err_export', 'Export failed. Try a smaller size or WebM.'), 'error'); ae('ta_export_err', { ms: Math.round(performance.now() - t0), code: (err && err.message || '').slice(0, 40), path: path }); }
        } finally {
            if (myRun) myRun.abort();   // idempotent after commit; cancel / failure → never bills
            exporting = false;
            if (els.cancel) els.cancel.style.display = 'none';
            setProgress(0, false);
            [els.lang, els.voice, els.size, els.format].forEach(function (e) { if (e) e.disabled = false; });
            if (els.audio) els.audio.disabled = !currentAudioBlob;
            if (els.export) els.export.disabled = !currentBuffer;
            if (els.speak) els.speak.disabled = false;
            // restore preview state
            renderer.setPixelRatio(prevPR);
            applyBackground();
            resizePreview();
        }
    }

    function onAudioDownload() {
        if (!currentAudioBlob) return;
        var name = 'talking-avatar-' + Date.now() + '.mp3';
        // AI Act art. 50(2): synthetic speech MP3 gets an ID3 AI-note at the
        // download boundary; marking failure falls back to the raw blob.
        var send = function (b) {
            b = b || currentAudioBlob;
            (window.__downloadFile || function (x, n) { var u = URL.createObjectURL(x); var a = document.createElement('a'); a.href = u; a.download = n; a.click(); URL.revokeObjectURL(u); })(b, name);
            ae('ta_audio_download', { bytes: b.size, dur: +durSec.toFixed(1) });
        };
        if (window.__aiMark) { window.__aiMark.mp3(currentAudioBlob, 'talking-avatar').then(send, function () { send(currentAudioBlob); }); }
        else { send(currentAudioBlob); }
    }

    /* ───────────────────────── voice UI ───────────────────────── */
    function altVoice(v) {
        var lang = els.lang.value, list = LANG_VOICES[lang] || [];
        for (var i = 0; i < list.length; i++) if (list[i][0] !== v) return list[i][0];
        return '';
    }
    function fillVoices() {
        var lang = els.lang.value, list = LANG_VOICES[lang] || LANG_VOICES.en;
        els.voice.innerHTML = '';
        list.forEach(function (v) { var o = document.createElement('option'); o.value = v[0]; o.textContent = v[1]; els.voice.appendChild(o); });
        // 29 server-side Silero voices — Russian only
        if (lang === 'ru' && SILERO.length) {
            var og = document.createElement('optgroup');
            og.label = L('voices_new', 'Новые голоса') + ' (' + SILERO.length + ')';
            SILERO.forEach(function (v) {
                var o = document.createElement('option');
                o.value = v.id;
                o.textContent = v.label + ' · ' + (v.g === 'F' || v.g === 'f' ? 'Ж' : 'М');
                og.appendChild(o);
            });
            els.voice.appendChild(og);
        }
    }
    function fillLangs() {
        var def = (window.__LOCALE && LANG_VOICES[window.__LOCALE]) ? window.__LOCALE : 'en';
        Object.keys(LANG_VOICES).forEach(function (code) { var o = document.createElement('option'); o.value = code; o.textContent = LANG_LABEL[code] || code; if (code === def) o.selected = true; els.lang.appendChild(o); });
    }

    function dispose() {
        disposed = true;
        if (tickId) cancelAnimationFrame(tickId);
        stopPreview();
        if (_gradTex) { try { _gradTex.dispose(); } catch (_) {} _gradTex = null; }
        if (audioCtx && audioCtx.state !== 'closed') { try { audioCtx.close(); } catch (_) {} }
    }

    function setAvatar(idx) {
        idx = parseInt(idx, 10) || 0;
        if (idx < 0 || idx >= AVATARS.length || idx === avatarIdx) return;
        avatarIdx = idx;
        stopPreview();
        if (els.stage) els.stage.classList.remove('ready');
        loadAvatar();
        ae('ta_avatar_switch', { id: AVATARS[idx].id });
        try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'avatar', AVATARS[idx].id); } catch (_) {}
    }

    /* ───────────────────────── boot ───────────────────────── */
    function boot() {
        els = {
            canvasWrap: $('ta-canvas'), stage: $('ta-stage'), status: $('ta-status'), retry: $('ta-retry'),
            text: $('ta-text'), lang: $('ta-lang'), voice: $('ta-voice'), speed: $('ta-speed'), speedVal: $('ta-speed-val'), pitch: $('ta-pitch'), pitchVal: $('ta-pitch-val'),
            bg: $('ta-bg'), bg2: $('ta-bg2'), bgmode: $('ta-bgmode'), size: $('ta-size'), format: $('ta-format'), zoom: $('ta-zoom'),
            captions: $('ta-captions'), caption: $('ta-caption'), avatar: $('ta-avatar'), avatarField: $('tav-avatar-field'),
            glbDrop: $('ta-glb-drop'), customLine: $('ta-custom-line'), customName: $('ta-custom-name'), customRemove: $('ta-custom-remove'),
            speak: $('ta-speak'), replay: $('ta-replay'), export: $('ta-export'), audio: $('ta-audio'), cancel: $('ta-cancel'),
            progWrap: $('ta-prog'), progBar: $('ta-prog-bar'), progPct: $('ta-prog-pct'), charCount: $('ta-charcount'),
            quota: $('ta-quota'), wavecard: $('ta-wavecard'), wave: $('ta-wave'), playpause: $('ta-playpause'),
            stressRow: $('ta-stress-row'), autoStress: $('ta-autostress'), stressBtn: $('ta-stress'), stressClear: $('ta-stress-clear'),
            stressBridge: $('ta-stress-bridge'), homo: $('ta-homo'), homoList: $('ta-homo-list'), homoCount: $('ta-homo-count')
        };
        if (!els.canvasWrap || !els.text) return;
        if (els.text.getAttribute('maxlength')) MAX_CHARS = parseInt(els.text.getAttribute('maxlength'), 10) || MAX_CHARS;
        // Per-tier allowance. Read SYNCHRONOUSLY first: if the bootstrap resolved
        // before this script parsed, `tier-bootstrap:ready` is long gone and a
        // listener alone would leave a paying user on the anon baseline forever
        // (the exact race /text-to-speech had to fix).
        applyTierChars(window.__tierLimits);
        document.addEventListener('tier-bootstrap:ready', function (e) { applyTierChars(e && e.detail); });

        try { if (window.Timbrica3D && Timbrica3D.init) Timbrica3D.init(SLUG); } catch (_) {}

        // Manual-expression engine API — drives the pose panel and is the empirical probe surface.
        window.__taMorph = {
            ready: function () { return !!(hasMorphs && morphNames.length); },
            names: function () { return morphNames.slice(); },
            has: function (n) { return !!morphByName[(n || '').toLowerCase()]; },
            set: function (n, v) { manualMorphs[(n || '').toLowerCase()] = Math.max(0, Math.min(1, +v || 0)); manualActive = true; },
            get: function (n) { return manualMorphs[(n || '').toLowerCase()] || 0; },
            clear: function (n) { delete manualMorphs[(n || '').toLowerCase()]; },
            setPose: function (rx, ry, rz) { manualPose = { rx: +rx || 0, ry: +ry || 0, rz: +rz || 0 }; manualActive = true; },
            getPose: function () { return manualPose ? { rx: manualPose.rx, ry: manualPose.ry, rz: manualPose.rz } : { rx: 0, ry: 0, rz: 0 }; },
            idle: function (on) { idleAnim = !!on; },
            active: function () { return manualActive; },
            reset: function () { manualMorphs = {}; manualPose = null; manualActive = false; },
            render: function () { if (renderer && scene && camera) { applyState(performance.now() / 1000, !previewing); renderer.render(scene, camera); } }
        };

        // Settings: load BEFORE populating selects so saved lang/voice survive.
        var savedLang = null, savedVoice = null;
        try { if (window.__toolSettings) { savedLang = window.__toolSettings.load(SLUG, 'lang', null); savedVoice = window.__toolSettings.load(SLUG, 'voice', null); } } catch (_) {}
        fillLangs();
        if (savedLang && LANG_VOICES[savedLang]) els.lang.value = savedLang;
        fillVoices();
        if (savedVoice) { var has = Array.prototype.some.call(els.voice.options, function (o) { return o.value === savedVoice; }); if (has) els.voice.value = savedVoice; }
        var wantCustom = false;
        try { if (window.__toolSettings) { var sAv = window.__toolSettings.load(SLUG, 'avatar', null); if (sAv) { wantCustom = (sAv === CUSTOM_ID); for (var ii = 0; ii < AVATARS.length; ii++) if (AVATARS[ii].id === sAv) avatarIdx = ii; } } } catch (_) {}

        initScene();
        // Saved choice was «my avatar» → bring it back from IndexedDB; if the bytes
        // are gone (cleared site data, another profile) fall back to the built-in.
        if (wantCustom) restoreCustomAvatar().then(function (ok) { if (!ok && !disposed) loadAvatar(); });
        else loadAvatar();

        els.lang.addEventListener('change', function () { fillVoices(); try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'lang', els.lang.value); } catch (_) {} });
        els.voice.addEventListener('change', function () { try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'voice', els.voice.value); } catch (_) {} });
        els.speak.addEventListener('click', onSpeak);
        if (els.replay) els.replay.addEventListener('click', function () { if (currentBuffer) playPreview(); });
        if (els.export) els.export.addEventListener('click', onExport);
        if (els.audio) els.audio.addEventListener('click', onAudioDownload);
        if (els.cancel) els.cancel.addEventListener('click', function () { if (exportAbort) exportAbort.abort(); });
        if (els.retry) els.retry.addEventListener('click', loadAvatar);
        // Voice-over strip: play/pause + waveform seek; tier-aware char quota line
        if (els.playpause) els.playpause.addEventListener('click', togglePlayPause);
        if (els.wave) els.wave.addEventListener('click', waveSeek);
        window.addEventListener('resize', function () { if (currentBuffer) waveDraw(); });
        quotaInit();
        // Workbench tabs (Script / Look / Face)
        var tabBtns = document.querySelectorAll('.tav-tab');
        Array.prototype.forEach.call(tabBtns, function (btn) {
            btn.addEventListener('click', function () {
                Array.prototype.forEach.call(tabBtns, function (b) {
                    b.classList.toggle('active', b === btn);
                    b.setAttribute('aria-selected', b === btn ? 'true' : 'false');
                });
                ['script', 'look', 'face'].forEach(function (tn) {
                    var pane = document.getElementById('tav-tab-' + tn);
                    if (pane) pane.style.display = (tn === btn.getAttribute('data-tab')) ? '' : 'none';
                });
                ae('ta_tab', { tab: btn.getAttribute('data-tab') });
            });
        });
        if (els.avatar) {
            refreshAvatarOptions();
            els.avatar.addEventListener('change', function () { setAvatar(els.avatar.value); });
        }
        if (els.glbDrop) {
            // Race-safe front door: a file picked before this deferred script ran
            // is replayed by the helper; a raw listener would have missed it.
            var onGlbPick = function (f) { adoptCustomFile(f, 'pick'); };
            if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(els.glbDrop, onGlbPick);
            else els.glbDrop.addEventListener('tool-uploader:file', function (e) { var d = e.detail || {}; onGlbPick(d.file || (d.files && d.files[0])); });
            els.glbDrop.addEventListener('tool-uploader:clear', function () { removeCustomAvatar('uploader'); });
        }
        if (els.customRemove) els.customRemove.addEventListener('click', function () { removeCustomAvatar('button'); });
        setCustomProbe({ state: 'none' });
        els.bg.addEventListener('input', applyBackground);
        if (els.bg2) els.bg2.addEventListener('input', applyBackground);
        if (els.bgmode) els.bgmode.addEventListener('change', function () { applyBackground(); try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'bgmode', els.bgmode.value); } catch (_) {} });
        els.size.addEventListener('change', resizePreview);
        if (els.zoom) els.zoom.addEventListener('input', function () { camZoom = parseFloat(els.zoom.value) || 1; frameCamera(); });
        if (els.speed && els.speedVal) { var upd = function () { els.speedVal.textContent = (els.speed.value > 0 ? '+' : '') + els.speed.value + '%'; }; els.speed.addEventListener('input', upd); upd(); }
        if (els.pitch && els.pitchVal) { var updp = function () { els.pitchVal.textContent = (els.pitch.value > 0 ? '+' : '') + els.pitch.value; }; els.pitch.addEventListener('input', updp); updp(); }
        if (els.charCount) { var cc = function () { var n = (els.text.value || '').length; els.charCount.textContent = n + ' / ' + MAX_CHARS; els.charCount.classList.toggle('tav-near-limit', n > MAX_CHARS * 0.9); }; els.text.addEventListener('input', cc); cc(); }

        // Stress UI (ударения) — reveal/hide follows text language + voice capability.
        if (els.stressRow) {
            els.text.addEventListener('input', function () {
                homoBypass = false;   // edits re-arm the pre-synthesis homograph check
                refreshStressUI();
                if (els.homo && els.homo.classList.contains('on')) scanHomographs();
            });
            ['keyup', 'click', 'focus', 'blur'].forEach(function (ev) { els.text.addEventListener(ev, updateStressBtnMode); });
            if (els.autoStress) els.autoStress.addEventListener('click', autoStress);
            if (els.stressBtn) els.stressBtn.addEventListener('click', manualStress);
            if (els.stressClear) els.stressClear.addEventListener('click', clearStress);
            if (els.stressBridge) els.stressBridge.addEventListener('click', bridgeToSilero);
            // registered AFTER the fillVoices/save listeners above → runs second, so
            // the voice list is already rebuilt when we re-evaluate capability.
            els.lang.addEventListener('change', refreshStressUI);
            els.voice.addEventListener('change', refreshStressUI);
            refreshStressUI();
        }

        // Persist + restore look settings.
        try {
            if (window.__toolSettings) {
                window.__toolSettings.bind(SLUG, els.speed, 'speed', '0');
                if (els.pitch) window.__toolSettings.bind(SLUG, els.pitch, 'pitch', '0');
                window.__toolSettings.bind(SLUG, els.bg, 'bg', els.bg.value);
                if (els.bg2) window.__toolSettings.bind(SLUG, els.bg2, 'bg2', els.bg2.value);
                if (els.bgmode) els.bgmode.value = window.__toolSettings.load(SLUG, 'bgmode', els.bgmode.value);
                window.__toolSettings.bind(SLUG, els.size, 'size', els.size.value);
                window.__toolSettings.bind(SLUG, els.format, 'format', els.format.value);
                if (els.zoom) window.__toolSettings.bind(SLUG, els.zoom, 'zoom', '1');
                if (els.captions) {
                    var sc = window.__toolSettings.load(SLUG, 'captions', '0');
                    els.captions.checked = (sc === '1' || sc === true);
                    els.captions.addEventListener('change', function () { try { window.__toolSettings.save(SLUG, 'captions', els.captions.checked ? '1' : '0'); } catch (_) {} });
                }
            }
        } catch (_) {}
        camZoom = parseFloat(els.zoom && els.zoom.value) || 1;
        applyBackground();
        frameCamera();

        window.addEventListener('beforeunload', dispose);
        window.addEventListener('pagehide', dispose);
        resizePreview();
    }

    // THREE.GLTFLoader и OrbitControls приезжают из <x-threejs-loader>, а не
    // соседними тегами: зависимость от ядра там выражена явно, потому что
    // порядок тегов её не переживает (Sentry GK — «THREE is not defined»).
    // Значит и ждать надо сигнал загрузчика, а не собственную позицию.
    function start() {
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
        else boot();
    }
    if (window.__threeReady && typeof window.__threeReady.then === 'function') {
        window.__threeReady.then(start);
    } else {
        start();
    }
})();
